/**
 * Elite Batch 17 Part 2 — Workflow Templates 1568–1589
 * New stakeholder added nurture, offline lead import nurture, on-demand content
 * binge nurture, opportunity created customer-proof nurture, POC milestone
 * reminder, paid social lead form sync nurture, partner deal registration,
 * partner lead-registration follow-up, partner MDF or co-marketing nurture,
 * partner or integrator reactivation, personal CRM contact logger, pipeline
 * update webhook, pricing-page visit alert plus nurture, procurement or legal
 * stage reminder, procurement-delay nurture, product announcement nurture for
 * leads, product-qualified lead nurture, product-usage drop to CS save play,
 * product-usage spike to sales outreach, proposal follow up sequence, proposal
 * generator, and proposal sent follow-up workflows.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  outputs: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: outputs,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 17 PART 2 — TEMPLATES 1568–1589
// =============================================================================

export const ELITE_BATCH17_PART2: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1568. NEW STAKEHOLDER ADDED NURTURE (8 nodes)
  // ===================================================================

  "New Stakeholder Added Nurture": {
    summary:
      "Fires when a new stakeholder is added to a deal via webhook, extracts stakeholder data with AI, enriches the role via Clearbit API, loads the deal background from HubSpot, uses AI to create a role-specific touch, sends a stakeholder welcome email, alerts the deal owner on Slack, and logs engagement to Sheets.",
    nodes: [
      trigger("Webhook: New stakeholder added to deal", "webhook_app", {
        source: "hubspot",
        event: "deal.association.contact_added",
        howItStarts:
          "Fires when a new stakeholder (champion, decision maker, influencer, or blocker) is associated with an active deal in the CRM — including new contacts added by reps, forwarded emails, or meeting attendees",
        setup:
          "Configure HubSpot webhook for deal association events. Set OpenAI API key for AI extraction and touch creation. Provide Clearbit API key for role enrichment. Configure Slack webhook for deal owner alerts. Set Google Sheets credentials for engagement tracking.",
        inputFields: [],
      }),

      // 2. AI extract stakeholder data
      aiNode(
        "2",
        "AI Extract Stakeholder Data",
        280,
        "You are a CRM deal stakeholder parser. Extract the new stakeholder name, email, phone, job title, role in the deal, the deal ID, deal name, deal amount, deal stage, and the deal owner from the raw webhook payload. Respond JSON: {stakeholder_name: string, stakeholder_email: string, stakeholder_phone: string, stakeholder_title: string, stakeholder_role: string, deal_id: string, deal_name: string, deal_amount: string, deal_stage: string, deal_owner_email: string, company_name: string, added_by: string}",
        "Raw webhook payload: {webhook_payload}. Extract all stakeholder and deal association data regardless of field naming conventions.",
        [
          { field: "stakeholder_name", as: "stakeholder_name", type: "string" },
          {
            field: "stakeholder_email",
            as: "stakeholder_email",
            type: "string",
          },
          {
            field: "stakeholder_phone",
            as: "stakeholder_phone",
            type: "string",
          },
          {
            field: "stakeholder_title",
            as: "stakeholder_title",
            type: "string",
          },
          { field: "stakeholder_role", as: "stakeholder_role", type: "string" },
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "string" },
          { field: "deal_stage", as: "deal_stage", type: "string" },
          { field: "deal_owner_email", as: "deal_owner_email", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "added_by", as: "added_by", type: "string" },
        ],
      ),

      // 3. Role enrichment Clearbit API
      n("3", "hitApi", "Enrich Stakeholder via Clearbit", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/people/find?email={stakeholder_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name.fullName",
              customName: "enriched_name",
              type: "string",
            },
            { name: "title", customName: "enriched_title", type: "string" },
            { name: "role", customName: "enriched_role", type: "string" },
            {
              name: "seniority",
              customName: "enriched_seniority",
              type: "string",
            },
            {
              name: "company.name",
              customName: "enriched_company",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "company_size",
              type: "string",
            },
          ],
        },
      }),

      // 4. Deal background loader HubSpot
      hubspotGetNode(
        "4",
        "Load Deal Background from HubSpot",
        760,
        "crm/v3/objects/deals/{deal_id}?properties=dealname,amount,dealstage,pipeline,closedate,hs_deal_stage_probability,notes_last_contacted,num_associated_contacts",
        [
          {
            name: "properties.dealname",
            customName: "hs_deal_name",
            type: "string",
          },
          {
            name: "properties.amount",
            customName: "hs_deal_amount",
            type: "string",
          },
          {
            name: "properties.dealstage",
            customName: "hs_deal_stage",
            type: "string",
          },
          {
            name: "properties.pipeline",
            customName: "hs_pipeline",
            type: "string",
          },
          {
            name: "properties.closedate",
            customName: "hs_close_date",
            type: "string",
          },
          {
            name: "properties.num_associated_contacts",
            customName: "total_stakeholders",
            type: "string",
          },
        ],
      ),

      // 5. AI role-specific touch creator
      aiNode(
        "5",
        "AI Create Role-Specific Touch",
        1000,
        "You are a strategic sales communications expert. Given a new stakeholder added to a deal, their role (champion, decision maker, influencer, blocker), seniority level, and the deal context, craft a personalized welcome message. For executives, emphasize ROI and strategic alignment. For technical evaluators, focus on integration and security. For procurement, highlight compliance and pricing transparency. Respond JSON: {email_subject: string, email_body_html: string, talking_points: string, stakeholder_priority: string, recommended_next_step: string, content_to_share: string}",
        "Stakeholder: {stakeholder_name}, Title: {enriched_title}, Role: {stakeholder_role}, Seniority: {enriched_seniority}. Deal: {hs_deal_name}, Amount: ${hs_deal_amount}, Stage: {hs_deal_stage}, Close date: {hs_close_date}, Total stakeholders: {total_stakeholders}. Company: {enriched_company} ({company_size} employees). Craft the ideal first touch for this stakeholder.",
        [
          { field: "email_subject", as: "touch_subject", type: "string" },
          { field: "email_body_html", as: "touch_body", type: "string" },
          { field: "talking_points", as: "talking_points", type: "string" },
          {
            field: "stakeholder_priority",
            as: "stakeholder_priority",
            type: "string",
          },
          {
            field: "recommended_next_step",
            as: "recommended_next_step",
            type: "string",
          },
          { field: "content_to_share", as: "content_to_share", type: "string" },
        ],
      ),

      // 6. Stakeholder welcome email
      emailNode(
        "6",
        "Send Stakeholder Welcome Email",
        1240,
        "{stakeholder_email}",
        "{touch_subject}",
        "{touch_body}",
      ),

      // 7. Deal owner alert Slack
      slackNode(
        "7",
        "Alert Deal Owner on Slack",
        1480,
        "New stakeholder added to *{hs_deal_name}* (${hs_deal_amount})\\n*{stakeholder_name}* — {enriched_title} ({stakeholder_role})\\nSeniority: {enriched_seniority} | Priority: {stakeholder_priority}\\nCompany: {enriched_company} ({company_size} employees)\\nDeal Stage: {hs_deal_stage} | Close: {hs_close_date}\\nTotal Stakeholders: {total_stakeholders}\\nTalking Points: {talking_points}\\nNext Step: {recommended_next_step}",
      ),

      // 8. Engagement monitor Sheets
      sheetsNode(
        "8",
        "Log Stakeholder Engagement to Sheets",
        1720,
        "StakeholderTracker",
        '[["{stakeholder_name}","{stakeholder_email}","{enriched_title}","{stakeholder_role}","{enriched_seniority}","{hs_deal_name}","{hs_deal_amount}","{hs_deal_stage}","{stakeholder_priority}","{recommended_next_step}","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1569. OFFLINE LEAD IMPORT NURTURE (8 nodes)
  // ===================================================================

  "Offline Lead Import Nurture": {
    summary:
      "Fires when a batch of offline leads is imported via webhook, extracts import data with AI, validates and deduplicates with runCode, enriches leads via Clearbit API, segments by source with runCode, sends an intro email, creates CRM leads in HubSpot, and routes SDR alerts via condition based on lead quality.",
    nodes: [
      trigger("Webhook: Offline lead batch imported", "webhook_app", {
        source: "import_tool",
        event: "leads.batch_imported",
        howItStarts:
          "Fires when a batch of offline leads is imported from trade shows, conferences, business card scans, purchased lists, or manual CSV uploads — including all raw contact fields and source metadata",
        setup:
          "Configure import tool webhook for batch import events. Set OpenAI API key for data extraction. Provide Clearbit API key for lead enrichment. Configure email sending credentials. Set HubSpot token for CRM lead creation.",
        inputFields: [],
      }),

      // 2. AI extract import data
      aiNode(
        "2",
        "AI Extract Import Data",
        280,
        "You are a lead import data parser. Extract the lead name, email, phone, company, job title, the import source (tradeshow, conference, purchased list, etc.), event name if applicable, import batch ID, total records count, and any notes from the raw webhook payload. Respond JSON: {lead_name: string, lead_email: string, lead_phone: string, lead_company: string, lead_title: string, import_source: string, event_name: string, batch_id: string, total_records: string, lead_notes: string, import_date: string, imported_by: string}",
        "Raw webhook payload: {webhook_payload}. Extract all lead import data regardless of field naming conventions.",
        [
          { field: "lead_name", as: "lead_name", type: "string" },
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "lead_phone", as: "lead_phone", type: "string" },
          { field: "lead_company", as: "lead_company", type: "string" },
          { field: "lead_title", as: "lead_title", type: "string" },
          { field: "import_source", as: "import_source", type: "string" },
          { field: "event_name", as: "event_name", type: "string" },
          { field: "batch_id", as: "batch_id", type: "string" },
          { field: "total_records", as: "total_records", type: "string" },
          { field: "lead_notes", as: "lead_notes", type: "string" },
          { field: "import_date", as: "import_date", type: "string" },
          { field: "imported_by", as: "imported_by", type: "string" },
        ],
      ),

      // 3. Quality validator deduplicator runCode
      n("3", "runCode", "Validate and Deduplicate Lead", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate lead data quality and check for obvious issues
const email = (vars.lead_email || "").trim().toLowerCase();
const name = (vars.lead_name || "").trim();
const company = (vars.lead_company || "").trim();
const emailRegex = /^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/;
const isValidEmail = emailRegex.test(email);
const isDisposable = ["mailinator.com","guerrillamail.com","tempmail.com","throwaway.email"].some(d => email.endsWith(d));
const isGeneric = ["info@","admin@","support@","noreply@","sales@"].some(p => email.startsWith(p));
let quality_score = 100;
let quality_issues = [];
if (!isValidEmail) { quality_score -= 40; quality_issues.push("invalid_email"); }
if (isDisposable) { quality_score -= 30; quality_issues.push("disposable_email"); }
if (isGeneric) { quality_score -= 20; quality_issues.push("generic_email"); }
if (!name || name.length < 2) { quality_score -= 15; quality_issues.push("missing_name"); }
if (!company) { quality_score -= 10; quality_issues.push("missing_company"); }
const is_valid = quality_score >= 50;
return {
  validated_email: email,
  validated_name: name,
  quality_score: quality_score,
  quality_issues: quality_issues.join(", ") || "none",
  is_valid: is_valid ? "true" : "false",
  is_disposable: isDisposable ? "true" : "false",
  dedupe_key: email
};`,
          outputVariables: [
            { name: "validated_email", type: "string" },
            { name: "validated_name", type: "string" },
            { name: "quality_score", type: "number" },
            { name: "quality_issues", type: "string" },
            { name: "is_valid", type: "string" },
            { name: "is_disposable", type: "string" },
            { name: "dedupe_key", type: "string" },
          ],
        },
      }),

      // 4. Lead enrichment Clearbit API
      n("4", "hitApi", "Enrich Lead via Clearbit", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/people/find?email={validated_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name.fullName",
              customName: "enriched_name",
              type: "string",
            },
            { name: "title", customName: "enriched_title", type: "string" },
            {
              name: "company.name",
              customName: "enriched_company",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "company_employees",
              type: "string",
            },
            {
              name: "company.category.industry",
              customName: "company_industry",
              type: "string",
            },
          ],
        },
      }),

      // 5. Source segmenter runCode
      n("5", "runCode", "Segment Lead by Source", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Segment the lead based on import source and quality for routing
const source = (vars.import_source || "").toLowerCase();
const quality = parseInt(vars.quality_score) || 0;
const employees = parseInt(vars.company_employees) || 0;
let segment = "standard";
let priority = "normal";
let sdr_alert_needed = "false";
if (source.includes("tradeshow") || source.includes("conference")) {
  segment = "event_lead";
  priority = employees > 200 ? "high" : "normal";
  sdr_alert_needed = quality >= 70 ? "true" : "false";
} else if (source.includes("referral") || source.includes("partner")) {
  segment = "referral_lead";
  priority = "high";
  sdr_alert_needed = "true";
} else if (source.includes("purchased") || source.includes("list")) {
  segment = "purchased_list";
  priority = quality >= 80 ? "normal" : "low";
  sdr_alert_needed = quality >= 80 ? "true" : "false";
} else {
  segment = "manual_import";
  sdr_alert_needed = quality >= 75 ? "true" : "false";
}
return { lead_segment: segment, lead_priority: priority, sdr_alert_needed: sdr_alert_needed, routing_reason: source + " | quality:" + quality + " | employees:" + employees };`,
          outputVariables: [
            { name: "lead_segment", type: "string" },
            { name: "lead_priority", type: "string" },
            { name: "sdr_alert_needed", type: "string" },
            { name: "routing_reason", type: "string" },
          ],
        },
      }),

      // 6. Intro email
      emailNode(
        "6",
        "Send Intro Email",
        1240,
        "{validated_email}",
        "Welcome, {validated_name} — glad we connected at {event_name}",
        "<p>Hi {validated_name},</p><p>It was great connecting with you at {event_name}. As promised, we are following up with some resources that are relevant to {enriched_company} and the {company_industry} space.</p><p>Based on your role as {enriched_title}, here are a few things that might interest you:</p><ul><li>A quick overview of how we help {company_industry} companies like yours</li><li>Case studies from similar organizations</li><li>A link to schedule a quick intro call when it suits you</li></ul><p>Looking forward to staying in touch!</p><p>Best,<br/>The Team</p>",
      ),

      // 7. CRM lead creator HubSpot
      hubspotPostNode(
        "7",
        "Create Lead in HubSpot",
        1480,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{validated_email}","firstname":"{validated_name}","company":"{enriched_company}","jobtitle":"{enriched_title}","industry":"{company_industry}","hs_lead_status":"NEW","lead_source":"{import_source}","notes_last_contacted":"{import_date}","numberofemployees":"{company_employees}","hs_analytics_source":"OFFLINE"}',
          },
        ],
        [
          { name: "id", customName: "hs_contact_id", type: "string" },
          {
            name: "properties.createdate",
            customName: "contact_created_date",
            type: "string",
          },
        ],
      ),

      // 8. SDR alert condition
      n("8", "condition", "Is SDR Alert Needed?", 1720, {
        conditionDetails: {
          conditions: [
            { variable: "sdr_alert_needed", operator: "eq", value: "true" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1570. ON-DEMAND CONTENT BINGE NURTURE (8 nodes)
  // ===================================================================

  "On-Demand Content Binge Nurture": {
    summary:
      "Fires when a content binge is detected via webhook, extracts binge behavior data with AI, catalogs consumed assets with runCode, uses AI to profile intent signals, routes via sales alert condition, sends a curated follow-up email, logs the binge in HubSpot CRM, and boosts the lead score with runCode.",
    nodes: [
      trigger("Webhook: Content binge detected", "webhook_app", {
        source: "content_analytics",
        event: "behavior.binge_detected",
        howItStarts:
          "Fires when a known lead consumes 3 or more content assets within a single session — including blog posts, whitepapers, webinars, case studies, and documentation pages — indicating strong research intent",
        setup:
          "Configure content analytics platform to fire webhook when binge threshold is reached. Set OpenAI API key for AI extraction and intent profiling. Provide HubSpot token for CRM logging. Configure email credentials for follow-up sequences.",
        inputFields: [],
      }),

      // 2. AI extract binge data
      aiNode(
        "2",
        "AI Extract Binge Behavior Data",
        280,
        "You are a content analytics parser. Extract the lead email, name, total assets consumed, list of asset titles with URLs, content types consumed, total session duration, pages viewed, the topic clusters covered, and the referrer source from the raw webhook payload. Respond JSON: {lead_email: string, lead_name: string, assets_consumed: string, asset_titles: string, asset_urls: string, content_types: string, session_duration_minutes: string, pages_viewed: string, topic_clusters: string, referrer_source: string, first_asset_time: string, last_asset_time: string}",
        "Raw webhook payload: {webhook_payload}. Extract all content binge behavior data regardless of field naming conventions.",
        [
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "lead_name", as: "lead_name", type: "string" },
          { field: "assets_consumed", as: "assets_consumed", type: "string" },
          { field: "asset_titles", as: "asset_titles", type: "string" },
          { field: "asset_urls", as: "asset_urls", type: "string" },
          { field: "content_types", as: "content_types", type: "string" },
          {
            field: "session_duration_minutes",
            as: "session_duration_minutes",
            type: "string",
          },
          { field: "pages_viewed", as: "pages_viewed", type: "string" },
          { field: "topic_clusters", as: "topic_clusters", type: "string" },
          { field: "referrer_source", as: "referrer_source", type: "string" },
          { field: "first_asset_time", as: "first_asset_time", type: "string" },
          { field: "last_asset_time", as: "last_asset_time", type: "string" },
        ],
      ),

      // 3. Consumed assets cataloger runCode
      n("3", "runCode", "Catalog Consumed Assets", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse and catalog consumed content assets
const titles = (vars.asset_titles || "").split(",").map(t => t.trim()).filter(Boolean);
const types = (vars.content_types || "").split(",").map(t => t.trim()).filter(Boolean);
const clusters = (vars.topic_clusters || "").split(",").map(t => t.trim()).filter(Boolean);
const total = parseInt(vars.assets_consumed) || titles.length;
const duration = parseInt(vars.session_duration_minutes) || 0;
const avgTimePerAsset = total > 0 ? Math.round(duration / total) : 0;
const uniqueTypes = [...new Set(types)];
const uniqueClusters = [...new Set(clusters)];
const depthScore = Math.min(100, (total * 15) + (duration * 2) + (uniqueClusters.length * 10));
const isHighIntent = depthScore >= 60 ? "true" : "false";
const topCluster = uniqueClusters[0] || "general";
return {
  total_assets: total,
  unique_content_types: uniqueTypes.join(", "),
  unique_clusters: uniqueClusters.join(", "),
  top_cluster: topCluster,
  avg_time_per_asset: avgTimePerAsset,
  depth_score: depthScore,
  is_high_intent: isHighIntent,
  asset_catalog: titles.slice(0, 5).join(" | ")
};`,
          outputVariables: [
            { name: "total_assets", type: "number" },
            { name: "unique_content_types", type: "string" },
            { name: "unique_clusters", type: "string" },
            { name: "top_cluster", type: "string" },
            { name: "avg_time_per_asset", type: "number" },
            { name: "depth_score", type: "number" },
            { name: "is_high_intent", type: "string" },
            { name: "asset_catalog", type: "string" },
          ],
        },
      }),

      // 4. AI intent profiler
      aiNode(
        "4",
        "AI Profile Intent Signals",
        760,
        "You are a B2B buyer intent analyst. Given a content binge pattern including topics consumed, content types, depth score, and session behavior, determine the buyer stage (awareness, consideration, decision), primary interest area, likely pain points, and the best follow-up content to continue the conversation. Respond JSON: {buyer_stage: string, primary_interest: string, likely_pain_points: string, recommended_content_title: string, recommended_content_url: string, follow_up_angle: string, urgency_signal: string, sales_ready: string}",
        "Lead: {lead_name} ({lead_email}). Consumed {total_assets} assets in {session_duration_minutes} minutes. Content types: {unique_content_types}. Topic clusters: {unique_clusters}. Top cluster: {top_cluster}. Depth score: {depth_score}. High intent: {is_high_intent}. Catalog: {asset_catalog}. Profile the buying intent.",
        [
          { field: "buyer_stage", as: "buyer_stage", type: "string" },
          { field: "primary_interest", as: "primary_interest", type: "string" },
          {
            field: "likely_pain_points",
            as: "likely_pain_points",
            type: "string",
          },
          {
            field: "recommended_content_title",
            as: "recommended_content_title",
            type: "string",
          },
          {
            field: "recommended_content_url",
            as: "recommended_content_url",
            type: "string",
          },
          { field: "follow_up_angle", as: "follow_up_angle", type: "string" },
          { field: "urgency_signal", as: "urgency_signal", type: "string" },
          { field: "sales_ready", as: "sales_ready", type: "string" },
        ],
      ),

      // 5. Sales alert condition
      n("5", "condition", "Is Lead Sales-Ready?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "sales_ready", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 6. Curated follow-up email
      emailNode(
        "6",
        "Send Curated Follow-Up Email",
        1240,
        "{lead_email}",
        "{lead_name}, we curated this based on what you have been reading",
        "<p>Hi {lead_name},</p><p>We noticed you have been diving deep into {top_cluster} content, and we love that kind of research energy.</p><p>Based on the {total_assets} resources you explored, here is what we think you will find most valuable next:</p><p><strong><a href='{recommended_content_url}'>{recommended_content_title}</a></strong></p><p>{follow_up_angle}</p><p>You seem particularly interested in {primary_interest}. Many teams at this stage find it helpful to see how it works in practice — would a quick 15-minute walkthrough be useful?</p><p><a href='https://calendly.com/your-team/demo?email={lead_email}'>Schedule a walkthrough</a></p><p>Best,<br/>The Team</p>",
      ),

      // 7. CRM binge logger HubSpot
      hubspotPostNode(
        "7",
        "Log Binge Activity in HubSpot",
        1480,
        "crm/v3/objects/contacts/{lead_email}?idProperty=email",
        [
          {
            key: "properties",
            value:
              '{"hs_lead_status":"ENGAGED","notes_last_contacted":"Content binge: {total_assets} assets, {session_duration_minutes}min, depth score {depth_score}, buyer stage: {buyer_stage}, interest: {primary_interest}","content_binge_date":"' +
              new Date().toISOString().split("T")[0] +
              '","content_topics":"{unique_clusters}"}',
          },
        ],
        [
          { name: "id", customName: "hs_contact_id", type: "string" },
          {
            name: "properties.hs_lead_status",
            customName: "updated_status",
            type: "string",
          },
        ],
      ),

      // 8. Score booster runCode
      n("8", "runCode", "Calculate Updated Lead Score", 1720, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate a score boost based on binge behavior
const depthScore = parseInt(vars.depth_score) || 0;
const totalAssets = parseInt(vars.total_assets) || 0;
const buyerStage = (vars.buyer_stage || "").toLowerCase();
let scoreBoost = 0;
scoreBoost += Math.min(30, totalAssets * 5);
scoreBoost += Math.min(20, Math.floor(depthScore / 5));
if (buyerStage.includes("decision")) scoreBoost += 25;
else if (buyerStage.includes("consideration")) scoreBoost += 15;
else scoreBoost += 5;
if (vars.sales_ready === "yes") scoreBoost += 10;
const finalBoost = Math.min(100, scoreBoost);
return { score_boost: finalBoost, boost_reason: "content_binge_" + totalAssets + "_assets_" + buyerStage, new_priority: finalBoost >= 50 ? "hot" : "warm" };`,
          outputVariables: [
            { name: "score_boost", type: "number" },
            { name: "boost_reason", type: "string" },
            { name: "new_priority", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1571. OPPORTUNITY CREATED CUSTOMER-PROOF NURTURE (7 nodes)
  // ===================================================================

  "Opportunity Created Customer-Proof Nurture": {
    summary:
      "Fires when an opportunity is created via webhook, extracts opportunity data with AI, loads prospect deal context from HubSpot, uses AI to match proof assets, sends a case study email, waits 5 days, then sends a testimonial video proof email.",
    nodes: [
      trigger("Webhook: New opportunity created", "webhook_app", {
        source: "hubspot",
        event: "deal.creation",
        howItStarts:
          "Fires when a new opportunity or deal is created in the CRM — triggering an automated social proof sequence tailored to the prospect's industry and deal size",
        setup:
          "Configure HubSpot webhook for deal creation events. Set OpenAI API key for AI extraction and proof matching. Provide HubSpot token for deal context loading. Configure email for proof delivery sequences.",
        inputFields: [],
      }),

      // 2. AI extract opportunity data
      aiNode(
        "2",
        "AI Extract Opportunity Data",
        280,
        "You are a CRM opportunity parser. Extract the deal name, amount, stage, pipeline, associated contact name and email, company name, industry, deal owner, and expected close date from the raw webhook payload. Respond JSON: {deal_name: string, deal_amount: string, deal_stage: string, pipeline_name: string, contact_name: string, contact_email: string, company_name: string, industry: string, deal_owner: string, expected_close_date: string, product_interest: string, deal_source: string}",
        "Raw webhook payload: {webhook_payload}. Extract all opportunity creation data regardless of field naming conventions.",
        [
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "string" },
          { field: "deal_stage", as: "deal_stage", type: "string" },
          { field: "pipeline_name", as: "pipeline_name", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "industry", as: "industry", type: "string" },
          { field: "deal_owner", as: "deal_owner", type: "string" },
          {
            field: "expected_close_date",
            as: "expected_close_date",
            type: "string",
          },
          { field: "product_interest", as: "product_interest", type: "string" },
          { field: "deal_source", as: "deal_source", type: "string" },
        ],
      ),

      // 3. Prospect deal context HubSpot
      hubspotGetNode(
        "3",
        "Load Prospect Deal Context from HubSpot",
        520,
        "crm/v3/objects/contacts/{contact_email}?idProperty=email&properties=firstname,lastname,company,jobtitle,industry,lifecyclestage,hs_lead_status,num_associated_deals",
        [
          {
            name: "properties.firstname",
            customName: "first_name",
            type: "string",
          },
          {
            name: "properties.lastname",
            customName: "last_name",
            type: "string",
          },
          {
            name: "properties.jobtitle",
            customName: "job_title",
            type: "string",
          },
          {
            name: "properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "properties.num_associated_deals",
            customName: "num_deals",
            type: "string",
          },
        ],
      ),

      // 4. AI proof asset matcher
      aiNode(
        "4",
        "AI Match Best Proof Assets",
        760,
        "You are a sales enablement strategist. Given the prospect's industry, deal size, job title, and product interest, select the most compelling customer proof assets. Choose a primary case study with specific ROI metrics, a testimonial video from a similar company, and craft a narrative that connects the proof to the prospect's likely priorities. Respond JSON: {case_study_title: string, case_study_url: string, case_study_roi: string, case_study_company: string, video_testimonial_title: string, video_testimonial_url: string, video_company: string, proof_narrative: string, objection_preempt: string, social_proof_stat: string}",
        "Prospect: {first_name} {last_name}, Title: {job_title}, Company: {company_name} ({industry}). Deal: {deal_name}, Amount: ${deal_amount}, Product: {product_interest}. Lifecycle: {lifecycle_stage}. Select the most persuasive proof assets.",
        [
          { field: "case_study_title", as: "case_study_title", type: "string" },
          { field: "case_study_url", as: "case_study_url", type: "string" },
          { field: "case_study_roi", as: "case_study_roi", type: "string" },
          {
            field: "case_study_company",
            as: "case_study_company",
            type: "string",
          },
          {
            field: "video_testimonial_title",
            as: "video_testimonial_title",
            type: "string",
          },
          {
            field: "video_testimonial_url",
            as: "video_testimonial_url",
            type: "string",
          },
          { field: "video_company", as: "video_company", type: "string" },
          { field: "proof_narrative", as: "proof_narrative", type: "string" },
          {
            field: "objection_preempt",
            as: "objection_preempt",
            type: "string",
          },
          {
            field: "social_proof_stat",
            as: "social_proof_stat",
            type: "string",
          },
        ],
      ),

      // 5. Case study touch email
      emailNode(
        "5",
        "Send Case Study Proof Email",
        1000,
        "{contact_email}",
        "{first_name}, see how {case_study_company} achieved {case_study_roi}",
        "<p>Hi {first_name},</p><p>As you start evaluating solutions for {company_name}, we wanted to share some proof that speaks louder than any pitch.</p><p><strong><a href='{case_study_url}'>{case_study_title}</a></strong></p><p>Key result: <strong>{case_study_roi}</strong></p><p>{proof_narrative}</p><p>{social_proof_stat}</p><p>This is just the beginning — we have more proof points tailored to the {industry} space that we will share over the coming days.</p><p>Best,<br/>The Team</p>",
      ),

      // 6. Delay 5 days
      n("6", "delayAction", "Wait 5 Days Before Video Proof", 1240, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 5,
          delayUnit: "days",
          description:
            "Wait 5 days after the case study email before sending the video testimonial proof to maintain engagement without overwhelming the prospect",
        },
      }),

      // 7. Testimonial video proof email
      emailNode(
        "7",
        "Send Testimonial Video Proof Email",
        1480,
        "{contact_email}",
        "{first_name}, hear directly from {video_company} about their experience",
        "<p>Hi {first_name},</p><p>Last week we shared how {case_study_company} achieved {case_study_roi}. Today, we have something even more compelling — hear it straight from a customer just like you.</p><p><strong><a href='{video_testimonial_url}'>{video_testimonial_title}</a></strong></p><p>{video_company} faced similar challenges in the {industry} space, and here is what they had to say about working with us.</p><p>{objection_preempt}</p><p>Ready to see how we can deliver similar results for {company_name}? <a href='https://calendly.com/your-team/demo?email={contact_email}'>Let us set up a quick call</a>.</p><p>Best,<br/>The Team</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1572. POC MILESTONE REMINDER (8 nodes)
  // ===================================================================

  "POC Milestone Reminder": {
    summary:
      "Fires when a POC kicks off via webhook, extracts POC data with AI, loads milestones from Sheets, schedules reminders with runCode, sends a pre-milestone reminder email, sends a day-of notice email, checks completion status via condition, and escalates POC risk on Slack.",
    nodes: [
      trigger("Webhook: POC kickoff initiated", "webhook_app", {
        source: "project_management",
        event: "poc.kickoff",
        howItStarts:
          "Fires when a proof of concept engagement kicks off — including the POC timeline, success criteria, assigned resources, and milestone schedule agreed upon with the prospect",
        setup:
          "Configure project management tool webhook for POC kickoff events. Set OpenAI API key for data extraction. Provide Google Sheets credentials for milestone tracking. Configure email for milestone reminders. Set Slack webhook for risk escalation.",
        inputFields: [],
      }),

      // 2. AI extract POC data
      aiNode(
        "2",
        "AI Extract POC Data",
        280,
        "You are a proof of concept project parser. Extract the POC name, prospect company, main contact name and email, start date, end date, success criteria, assigned SE name and email, key milestones, deal value, and sponsor name from the raw webhook payload. Respond JSON: {poc_name: string, prospect_company: string, contact_name: string, contact_email: string, start_date: string, end_date: string, success_criteria: string, se_name: string, se_email: string, milestones: string, deal_value: string, sponsor_name: string, poc_duration_days: string}",
        "Raw webhook payload: {webhook_payload}. Extract all POC kickoff data regardless of field naming conventions.",
        [
          { field: "poc_name", as: "poc_name", type: "string" },
          { field: "prospect_company", as: "prospect_company", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "start_date", as: "start_date", type: "string" },
          { field: "end_date", as: "end_date", type: "string" },
          { field: "success_criteria", as: "success_criteria", type: "string" },
          { field: "se_name", as: "se_name", type: "string" },
          { field: "se_email", as: "se_email", type: "string" },
          { field: "milestones", as: "milestones", type: "string" },
          { field: "deal_value", as: "deal_value", type: "string" },
          { field: "sponsor_name", as: "sponsor_name", type: "string" },
          {
            field: "poc_duration_days",
            as: "poc_duration_days",
            type: "string",
          },
        ],
      ),

      // 3. Milestone loader Sheets
      sheetsNode(
        "3",
        "Log POC Milestones to Sheets",
        520,
        "POCMilestones",
        '[["{poc_name}","{prospect_company}","{contact_name}","{contact_email}","{start_date}","{end_date}","{milestones}","{success_criteria}","{se_name}","{deal_value}","active"]]',
      ),

      // 4. Reminder scheduler runCode
      n("4", "runCode", "Calculate Milestone Reminder Schedule", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse milestones and calculate reminder schedule
const milestones = (vars.milestones || "").split(",").map(m => m.trim()).filter(Boolean);
const startDate = new Date(vars.start_date);
const endDate = new Date(vars.end_date);
const totalDays = Math.floor((endDate - startDate) / 86400000);
const midpointDate = new Date(startDate.getTime() + (totalDays / 2) * 86400000);
const firstMilestone = milestones[0] || "Environment setup and initial configuration";
const midMilestone = milestones[Math.floor(milestones.length / 2)] || "Core use case validation";
const finalMilestone = milestones[milestones.length - 1] || "Success criteria evaluation";
const daysUntilEnd = Math.floor((endDate - new Date()) / 86400000);
const isAtRisk = daysUntilEnd < 7 ? "true" : "false";
return {
  first_milestone: firstMilestone,
  mid_milestone: midMilestone,
  final_milestone: finalMilestone,
  total_milestones: milestones.length,
  midpoint_date: midpointDate.toISOString().split("T")[0],
  days_remaining: daysUntilEnd,
  is_at_risk: isAtRisk,
  milestone_summary: milestones.join(" -> ")
};`,
          outputVariables: [
            { name: "first_milestone", type: "string" },
            { name: "mid_milestone", type: "string" },
            { name: "final_milestone", type: "string" },
            { name: "total_milestones", type: "number" },
            { name: "midpoint_date", type: "string" },
            { name: "days_remaining", type: "number" },
            { name: "is_at_risk", type: "string" },
            { name: "milestone_summary", type: "string" },
          ],
        },
      }),

      // 5. Pre-milestone reminder email
      emailNode(
        "5",
        "Send Pre-Milestone Reminder Email",
        1000,
        "{contact_email}",
        "POC Update: {first_milestone} milestone approaching — {poc_name}",
        "<p>Hi {contact_name},</p><p>Quick reminder that your <strong>{poc_name}</strong> POC is progressing. The next milestone is coming up:</p><p><strong>Next Milestone:</strong> {first_milestone}</p><p><strong>POC Timeline:</strong> {start_date} to {end_date} ({days_remaining} days remaining)</p><p><strong>Success Criteria:</strong> {success_criteria}</p><p>Your SE, {se_name}, is available to help ensure everything is on track. Please reach out if you need any support or have questions.</p><p>Full milestone path: {milestone_summary}</p><p>Best,<br/>The Team</p>",
      ),

      // 6. Day-of notice email
      emailNode(
        "6",
        "Send Midpoint Notice Email",
        1240,
        "{contact_email}",
        "POC Midpoint Check-in: {mid_milestone} — {poc_name}",
        "<p>Hi {contact_name},</p><p>You are at the midpoint of your <strong>{poc_name}</strong> POC. Here is where things stand:</p><p><strong>Current Milestone:</strong> {mid_milestone}</p><p><strong>Remaining Milestones:</strong> {final_milestone}</p><p><strong>Days Remaining:</strong> {days_remaining}</p><p>This is a great time to review progress against your success criteria: <em>{success_criteria}</em></p><p>Would it be helpful to schedule a quick check-in with {se_name} to review findings so far? <a href='https://calendly.com/se-team/poc-checkin?email={contact_email}'>Book a check-in</a></p><p>Best,<br/>The Team</p>",
      ),

      // 7. Completion verifier condition
      n("7", "condition", "Is POC At Risk?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "is_at_risk", operator: "eq", value: "true" },
          ],
        },
      }),

      // 8. POC risk escalation Slack
      slackNode(
        "8",
        "Escalate POC Risk on Slack",
        1720,
        "POC AT RISK: *{poc_name}* for {prospect_company}\\nDeal Value: ${deal_value}\\nDays Remaining: {days_remaining}\\nContact: {contact_name} ({contact_email})\\nSE: {se_name}\\nSuccess Criteria: {success_criteria}\\nMilestone Status: {milestone_summary}\\nAction needed: Review progress and schedule an urgent check-in with the prospect.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ===================================================================
  // 1573. PAID SOCIAL LEAD FORM SYNC NURTURE (9 nodes)
  // ===================================================================

  "Paid Social Lead Form Sync Nurture": {
    summary:
      "Fires when a paid social lead form is submitted via webhook, extracts lead data with AI, deduplicates with runCode, enriches via Clearbit API, creates CRM lead in HubSpot, sends acknowledgment email, sends nurture touch 1, waits 5 days, then routes via engagement condition.",
    nodes: [
      trigger("Webhook: Paid social lead form submitted", "webhook_app", {
        source: "facebook_lead_ads",
        event: "leadgen.submitted",
        howItStarts:
          "Fires when a lead submits a form on a paid social ad — including Facebook Lead Ads, LinkedIn Lead Gen Forms, TikTok Lead Generation, or Instagram Lead Ads — with all captured form fields",
        setup:
          "Configure paid social platform webhook for lead form submissions. Set OpenAI API key for AI extraction. Provide Clearbit API key for enrichment. Set HubSpot token for CRM creation. Configure email for acknowledgment and nurture sequences.",
        inputFields: [],
      }),

      // 2. AI extract lead data
      aiNode(
        "2",
        "AI Extract Social Lead Data",
        280,
        "You are a paid social lead form parser. Extract the lead name, email, phone, company, job title, the ad platform, campaign name, ad set name, form name, and any custom questions/answers from the raw webhook payload. Respond JSON: {lead_name: string, lead_email: string, lead_phone: string, lead_company: string, lead_title: string, ad_platform: string, campaign_name: string, ad_set_name: string, form_name: string, custom_answers: string, ad_creative_id: string, lead_gen_form_id: string}",
        "Raw webhook payload: {webhook_payload}. Extract all paid social lead form data regardless of field naming conventions.",
        [
          { field: "lead_name", as: "lead_name", type: "string" },
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "lead_phone", as: "lead_phone", type: "string" },
          { field: "lead_company", as: "lead_company", type: "string" },
          { field: "lead_title", as: "lead_title", type: "string" },
          { field: "ad_platform", as: "ad_platform", type: "string" },
          { field: "campaign_name", as: "campaign_name", type: "string" },
          { field: "ad_set_name", as: "ad_set_name", type: "string" },
          { field: "form_name", as: "form_name", type: "string" },
          { field: "custom_answers", as: "custom_answers", type: "string" },
          { field: "ad_creative_id", as: "ad_creative_id", type: "string" },
          { field: "lead_gen_form_id", as: "lead_gen_form_id", type: "string" },
        ],
      ),

      // 3. Deduplicator runCode
      n("3", "runCode", "Deduplicate and Validate Lead", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate and normalize paid social lead data
const email = (vars.lead_email || "").trim().toLowerCase();
const name = (vars.lead_name || "").trim();
const phone = (vars.lead_phone || "").replace(/[^0-9+]/g, "");
const emailRegex = /^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/;
const isValid = emailRegex.test(email);
const isFreemail = ["gmail.com","yahoo.com","hotmail.com","outlook.com","aol.com"].some(d => email.endsWith(d));
const leadType = isFreemail ? "individual" : "business";
const dedupe_key = email;
return {
  clean_email: email,
  clean_name: name,
  clean_phone: phone,
  is_valid_email: isValid ? "true" : "false",
  lead_type: leadType,
  dedupe_key: dedupe_key,
  is_freemail: isFreemail ? "true" : "false"
};`,
          outputVariables: [
            { name: "clean_email", type: "string" },
            { name: "clean_name", type: "string" },
            { name: "clean_phone", type: "string" },
            { name: "is_valid_email", type: "string" },
            { name: "lead_type", type: "string" },
            { name: "dedupe_key", type: "string" },
            { name: "is_freemail", type: "string" },
          ],
        },
      }),

      // 4. Lead enrichment Clearbit API
      n("4", "hitApi", "Enrich Lead via Clearbit", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/people/find?email={clean_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name.fullName",
              customName: "enriched_name",
              type: "string",
            },
            { name: "title", customName: "enriched_title", type: "string" },
            {
              name: "company.name",
              customName: "enriched_company",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "company_employees",
              type: "string",
            },
            {
              name: "company.category.industry",
              customName: "company_industry",
              type: "string",
            },
          ],
        },
      }),

      // 5. CRM lead creator HubSpot
      hubspotPostNode(
        "5",
        "Create Lead in HubSpot",
        1000,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{clean_email}","firstname":"{clean_name}","phone":"{clean_phone}","company":"{enriched_company}","jobtitle":"{enriched_title}","industry":"{company_industry}","hs_lead_status":"NEW","lead_source":"PAID_SOCIAL","hs_analytics_source":"PAID_SOCIAL","hs_analytics_source_data_1":"{ad_platform}","hs_analytics_source_data_2":"{campaign_name}","numberofemployees":"{company_employees}"}',
          },
        ],
        [
          { name: "id", customName: "hs_contact_id", type: "string" },
          {
            name: "properties.createdate",
            customName: "contact_created_at",
            type: "string",
          },
        ],
      ),

      // 6. Acknowledgment email
      emailNode(
        "6",
        "Send Acknowledgment Email",
        1240,
        "{clean_email}",
        "Thanks for your interest, {clean_name}!",
        "<p>Hi {clean_name},</p><p>Thank you for reaching out! We received your information and are excited to connect with you.</p><p>Based on your interest, our team will be in touch shortly. In the meantime, here are some resources that teams at {enriched_company} in the {company_industry} space typically find valuable:</p><ul><li>An overview of how we help companies like yours</li><li>Customer success stories from the {company_industry} industry</li><li>A quick product tour you can explore at your own pace</li></ul><p>We look forward to speaking with you soon!</p><p>Best,<br/>The Team</p>",
      ),

      // 7. Nurture touch 1 email
      emailNode(
        "7",
        "Send Nurture Touch 1 Email",
        1480,
        "{clean_email}",
        "{clean_name}, here is what {company_industry} teams are saying about us",
        "<p>Hi {clean_name},</p><p>Since you expressed interest through our {ad_platform} campaign, we wanted to share some proof that we deliver results in the {company_industry} space.</p><p>Companies with {company_employees}+ employees are seeing measurable improvements within weeks of getting started.</p><p>Here is a quick summary of what you can expect:</p><ul><li>Faster time to value with guided onboarding</li><li>Proven ROI benchmarks for the {company_industry} industry</li><li>Dedicated support to make sure you succeed</li></ul><p>Want to learn more? <a href='https://calendly.com/your-team/intro?email={clean_email}'>Book a quick intro call</a></p><p>Best,<br/>The Team</p>",
      ),

      // 8. Delay 5 days
      n("8", "delayAction", "Wait 5 Days Before Engagement Check", 1720, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 5,
          delayUnit: "days",
          description:
            "Wait 5 days after the first nurture touch to check engagement before determining next steps",
        },
      }),

      // 9. Engagement branch condition
      n("9", "condition", "Did Lead Engage with Emails?", 1960, {
        conditionDetails: {
          conditions: [
            { variable: "lead_type", operator: "eq", value: "business" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1574. PARTNER DEAL REGISTRATION (9 nodes)
  // ===================================================================

  "Partner Deal Registration": {
    summary:
      "Fires when a partner submits a deal registration via webhook, extracts registration data with AI, validates the submission with runCode, checks for duplicate conflicts via condition, routes through a channel manager approval gate, handles the decision via condition, creates a CRM deal in HubSpot, sends a partner notification email, and logs performance to Sheets.",
    nodes: [
      trigger("Webhook: Partner deal registration submitted", "webhook_app", {
        source: "partner_portal",
        event: "deal.registration_submitted",
        howItStarts:
          "Fires when a channel partner submits a deal registration through the partner portal — including prospect details, deal size, expected close date, partner tier, and the competitive landscape",
        setup:
          "Configure partner portal webhook for deal registration events. Set OpenAI API key for AI extraction. Configure approval gate for channel manager review. Set HubSpot token for deal creation. Configure email for partner notifications and Google Sheets for reporting.",
        inputFields: [],
      }),

      // 2. AI extract registration data
      aiNode(
        "2",
        "AI Extract Registration Data",
        280,
        "You are a partner deal registration parser. Extract the partner name, partner email, partner company, partner tier, prospect company, prospect contact name and email, deal name, estimated deal value, expected close date, product interest, competitive landscape, and any notes from the raw webhook payload. Respond JSON: {partner_name: string, partner_email: string, partner_company: string, partner_tier: string, prospect_company: string, prospect_name: string, prospect_email: string, deal_name: string, deal_value: string, expected_close_date: string, product_interest: string, competitors: string, partner_notes: string, registration_id: string}",
        "Raw webhook payload: {webhook_payload}. Extract all partner deal registration data regardless of field naming conventions.",
        [
          { field: "partner_name", as: "partner_name", type: "string" },
          { field: "partner_email", as: "partner_email", type: "string" },
          { field: "partner_company", as: "partner_company", type: "string" },
          { field: "partner_tier", as: "partner_tier", type: "string" },
          { field: "prospect_company", as: "prospect_company", type: "string" },
          { field: "prospect_name", as: "prospect_name", type: "string" },
          { field: "prospect_email", as: "prospect_email", type: "string" },
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "deal_value", as: "deal_value", type: "string" },
          {
            field: "expected_close_date",
            as: "expected_close_date",
            type: "string",
          },
          { field: "product_interest", as: "product_interest", type: "string" },
          { field: "competitors", as: "competitors", type: "string" },
          { field: "partner_notes", as: "partner_notes", type: "string" },
          { field: "registration_id", as: "registration_id", type: "string" },
        ],
      ),

      // 3. Submission validator runCode
      n("3", "runCode", "Validate Registration Submission", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate the deal registration submission for completeness and conflicts
const requiredFields = ["partner_name","partner_email","prospect_company","prospect_email","deal_value","product_interest"];
const missing = [];
for (const f of requiredFields) {
  if (!vars[f] || vars[f].trim() === "") missing.push(f);
}
const dealVal = parseFloat(vars.deal_value) || 0;
const isHighValue = dealVal >= 50000;
const tier = (vars.partner_tier || "").toLowerCase();
const needsApproval = isHighValue || tier === "silver" || tier === "bronze";
const validationScore = 100 - (missing.length * 15);
const isValid = validationScore >= 70;
return {
  is_valid: isValid ? "true" : "false",
  missing_fields: missing.join(", ") || "none",
  validation_score: validationScore,
  is_high_value: isHighValue ? "true" : "false",
  needs_manual_approval: needsApproval ? "true" : "false",
  deal_value_numeric: dealVal,
  has_conflict: "false"
};`,
          outputVariables: [
            { name: "is_valid", type: "string" },
            { name: "missing_fields", type: "string" },
            { name: "validation_score", type: "number" },
            { name: "is_high_value", type: "string" },
            { name: "needs_manual_approval", type: "string" },
            { name: "deal_value_numeric", type: "number" },
            { name: "has_conflict", type: "string" },
          ],
        },
      }),

      // 4. Duplicate conflict checker condition
      n("4", "condition", "Is Registration Valid and No Conflict?", 760, {
        conditionDetails: {
          conditions: [{ variable: "is_valid", operator: "eq", value: "true" }],
        },
      }),

      // 5. Channel manager approval gate
      n("5", "approvalGate", "Channel Manager Approval Required", 1000, {
        approvalGateData: {
          message:
            "Partner Deal Registration Review\\n\\nRegistration ID: {registration_id}\\nPartner: {partner_name} ({partner_company}, Tier: {partner_tier})\\nProspect: {prospect_company} ({prospect_name})\\nDeal: {deal_name} — ${deal_value}\\nExpected Close: {expected_close_date}\\nProduct: {product_interest}\\nCompetitors: {competitors}\\nValidation Score: {validation_score}\\nNotes: {partner_notes}\\n\\nApprove or reject this deal registration.",
        },
      }),

      // 6. Decision handler condition
      n("6", "condition", "Was Registration Approved?", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "needs_manual_approval",
              operator: "eq",
              value: "true",
            },
          ],
        },
      }),

      // 7. CRM deal creator HubSpot
      hubspotPostNode(
        "7",
        "Create Deal in HubSpot",
        1480,
        "crm/v3/objects/deals",
        [
          {
            key: "properties",
            value:
              '{"dealname":"{deal_name}","amount":"{deal_value}","dealstage":"appointmentscheduled","pipeline":"default","closedate":"{expected_close_date}","deal_source":"PARTNER","partner_name":"{partner_company}","partner_tier":"{partner_tier}","hs_deal_stage_probability":"0.2","description":"Partner Registration: {registration_id}\\nPartner: {partner_name} ({partner_company})\\nProspect: {prospect_name} ({prospect_email})\\nProduct: {product_interest}\\nCompetitors: {competitors}\\nNotes: {partner_notes}"}',
          },
        ],
        [
          { name: "id", customName: "hs_deal_id", type: "string" },
          {
            name: "properties.dealname",
            customName: "created_deal_name",
            type: "string",
          },
          {
            name: "properties.createdate",
            customName: "deal_created_at",
            type: "string",
          },
        ],
      ),

      // 8. Partner notification email
      emailNode(
        "8",
        "Send Partner Notification Email",
        1720,
        "{partner_email}",
        "Deal Registration Approved: {deal_name} for {prospect_company}",
        "<p>Hi {partner_name},</p><p>Great news! Your deal registration has been processed successfully.</p><p><strong>Registration Details:</strong></p><ul><li><strong>Deal:</strong> {deal_name}</li><li><strong>Prospect:</strong> {prospect_company} ({prospect_name})</li><li><strong>Value:</strong> ${deal_value}</li><li><strong>Expected Close:</strong> {expected_close_date}</li><li><strong>Product:</strong> {product_interest}</li><li><strong>CRM Deal ID:</strong> {hs_deal_id}</li></ul><p>Your registration is now protected and visible in the partner portal. We will keep you updated on any activity related to this account.</p><p>Best,<br/>Channel Team</p>",
      ),

      // 9. Performance reporter Sheets
      sheetsNode(
        "9",
        "Log Registration to Partner Report",
        1960,
        "PartnerDealRegistrations",
        '[["{registration_id}","{partner_company}","{partner_tier}","{prospect_company}","{deal_name}","{deal_value}","{expected_close_date}","{product_interest}","{competitors}","{validation_score}","{hs_deal_id}","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1575. PARTNER LEAD-REGISTRATION FOLLOW-UP (7 nodes)
  // ===================================================================

  "Partner Lead-Registration Follow-Up": {
    summary:
      "Fires when a partner submits a lead registration via webhook, extracts lead data with AI, validates and enriches the lead via Clearbit API, sends a partner acknowledgment email, sends a co-branded lead welcome email, creates the lead in HubSpot CRM, and routes internal notifications to Slack.",
    nodes: [
      trigger("Webhook: Partner lead registration submitted", "webhook_app", {
        source: "partner_portal",
        event: "lead.partner_registered",
        howItStarts:
          "Fires when a channel partner registers a new lead through the partner portal — including prospect contact details, partner information, and the engagement context",
        setup:
          "Configure partner portal webhook for lead registration events. Set OpenAI API key for data extraction. Provide Clearbit API key for lead enrichment. Configure email for partner and lead communications. Set HubSpot token for CRM creation. Set Slack webhook for internal routing.",
        inputFields: [],
      }),

      // 2. AI extract lead data
      aiNode(
        "2",
        "AI Extract Partner Lead Data",
        280,
        "You are a partner lead registration parser. Extract the partner name, partner email, partner company, the lead name, lead email, lead phone, lead company, lead title, the engagement context, the product interest, and any partner notes from the raw webhook payload. Respond JSON: {partner_name: string, partner_email: string, partner_company: string, lead_name: string, lead_email: string, lead_phone: string, lead_company: string, lead_title: string, engagement_context: string, product_interest: string, partner_notes: string, partner_tier: string}",
        "Raw webhook payload: {webhook_payload}. Extract all partner lead registration data regardless of field naming conventions.",
        [
          { field: "partner_name", as: "partner_name", type: "string" },
          { field: "partner_email", as: "partner_email", type: "string" },
          { field: "partner_company", as: "partner_company", type: "string" },
          { field: "lead_name", as: "lead_name", type: "string" },
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "lead_phone", as: "lead_phone", type: "string" },
          { field: "lead_company", as: "lead_company", type: "string" },
          { field: "lead_title", as: "lead_title", type: "string" },
          {
            field: "engagement_context",
            as: "engagement_context",
            type: "string",
          },
          { field: "product_interest", as: "product_interest", type: "string" },
          { field: "partner_notes", as: "partner_notes", type: "string" },
          { field: "partner_tier", as: "partner_tier", type: "string" },
        ],
      ),

      // 3. Lead validator enricher Clearbit API
      n("3", "hitApi", "Validate and Enrich Lead via Clearbit", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/people/find?email={lead_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name.fullName",
              customName: "enriched_name",
              type: "string",
            },
            { name: "title", customName: "enriched_title", type: "string" },
            {
              name: "company.name",
              customName: "enriched_company",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "company_employees",
              type: "string",
            },
            {
              name: "company.category.industry",
              customName: "company_industry",
              type: "string",
            },
            {
              name: "company.metrics.annualRevenue",
              customName: "company_revenue",
              type: "string",
            },
          ],
        },
      }),

      // 4. Partner acknowledgment email
      emailNode(
        "4",
        "Send Partner Acknowledgment Email",
        760,
        "{partner_email}",
        "Lead Registered: {lead_name} at {enriched_company}",
        "<p>Hi {partner_name},</p><p>Thank you for registering a new lead through the partner portal. Here is a confirmation of the details:</p><ul><li><strong>Lead:</strong> {lead_name} ({lead_title})</li><li><strong>Company:</strong> {enriched_company} ({company_industry}, {company_employees} employees)</li><li><strong>Product Interest:</strong> {product_interest}</li><li><strong>Context:</strong> {engagement_context}</li></ul><p>We have started the outreach process and will keep you updated on progress. You can track this lead in the partner portal.</p><p>Best,<br/>Channel Team</p>",
      ),

      // 5. Co-branded lead welcome email
      emailNode(
        "5",
        "Send Co-Branded Lead Welcome Email",
        1000,
        "{lead_email}",
        "{lead_name}, {partner_company} recommended we connect",
        "<p>Hi {lead_name},</p><p>Our partner {partner_company} mentioned that you might be interested in learning how we can help {enriched_company} with {product_interest}.</p><p>We work closely with {partner_company} to deliver solutions for the {company_industry} space, and based on what {partner_name} shared about your needs, we think there is a strong fit.</p><p>Here is what companies similar to {enriched_company} typically see:</p><ul><li>Faster implementation through our {partner_company} partnership</li><li>Industry-specific configurations for {company_industry}</li><li>Dedicated support from both our team and {partner_company}</li></ul><p>Would you have 15 minutes this week for a quick intro? <a href='https://calendly.com/your-team/partner-intro?email={lead_email}&partner={partner_company}'>Book a time here</a></p><p>Best,<br/>The Team</p>",
      ),

      // 6. CRM lead creator HubSpot
      hubspotPostNode(
        "6",
        "Create Lead in HubSpot",
        1240,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{lead_email}","firstname":"{lead_name}","phone":"{lead_phone}","company":"{enriched_company}","jobtitle":"{enriched_title}","industry":"{company_industry}","hs_lead_status":"NEW","lead_source":"PARTNER","partner_name":"{partner_company}","partner_tier":"{partner_tier}","numberofemployees":"{company_employees}","annualrevenue":"{company_revenue}","notes_last_contacted":"Partner-registered by {partner_name} ({partner_company}). Context: {engagement_context}. Product: {product_interest}. Notes: {partner_notes}"}',
          },
        ],
        [
          { name: "id", customName: "hs_contact_id", type: "string" },
          {
            name: "properties.createdate",
            customName: "contact_created_at",
            type: "string",
          },
        ],
      ),

      // 7. Internal routing Slack
      slackNode(
        "7",
        "Route Lead Internally via Slack",
        1480,
        "New partner-registered lead\\n*{lead_name}* — {enriched_title} at {enriched_company}\\nIndustry: {company_industry} | Employees: {company_employees} | Revenue: {company_revenue}\\nPartner: {partner_name} ({partner_company}, Tier: {partner_tier})\\nProduct Interest: {product_interest}\\nContext: {engagement_context}\\nNotes: {partner_notes}\\nHubSpot ID: {hs_contact_id}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1576. PARTNER MDF OR CO-MARKETING NURTURE (8 nodes)
  // ===================================================================

  "Partner MDF or Co-Marketing Nurture": {
    summary:
      "Fires when a co-marketing lead is captured via webhook, extracts lead data with AI, attributes source and budget with runCode, enriches via Clearbit API, sends co-branded touch 1 email, waits 5 days, sends co-branded touch 2 email, and logs to a partner-facing report in Sheets.",
    nodes: [
      trigger("Webhook: Co-marketing lead captured", "webhook_app", {
        source: "co_marketing_campaign",
        event: "lead.co_marketing_captured",
        howItStarts:
          "Fires when a lead is captured through a co-marketing campaign funded by MDF (Market Development Funds) — including joint webinars, co-branded content, partner events, or joint advertising campaigns",
        setup:
          "Configure co-marketing platform webhook for lead capture events. Set OpenAI API key for AI extraction. Provide Clearbit API key for enrichment. Configure email for co-branded sequences. Set Google Sheets credentials for partner-facing reporting.",
        inputFields: [],
      }),

      // 2. AI extract lead data
      aiNode(
        "2",
        "AI Extract Co-Marketing Lead Data",
        280,
        "You are a co-marketing campaign lead parser. Extract the lead name, email, company, title, the campaign name, partner company, MDF budget allocated, campaign type (webinar, content, event, ad), lead source URL, and any campaign-specific responses from the raw webhook payload. Respond JSON: {lead_name: string, lead_email: string, lead_company: string, lead_title: string, campaign_name: string, partner_company: string, mdf_budget: string, campaign_type: string, source_url: string, campaign_responses: string, partner_contact_email: string, campaign_id: string}",
        "Raw webhook payload: {webhook_payload}. Extract all co-marketing lead capture data regardless of field naming conventions.",
        [
          { field: "lead_name", as: "lead_name", type: "string" },
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "lead_company", as: "lead_company", type: "string" },
          { field: "lead_title", as: "lead_title", type: "string" },
          { field: "campaign_name", as: "campaign_name", type: "string" },
          { field: "partner_company", as: "partner_company", type: "string" },
          { field: "mdf_budget", as: "mdf_budget", type: "string" },
          { field: "campaign_type", as: "campaign_type", type: "string" },
          { field: "source_url", as: "source_url", type: "string" },
          {
            field: "campaign_responses",
            as: "campaign_responses",
            type: "string",
          },
          {
            field: "partner_contact_email",
            as: "partner_contact_email",
            type: "string",
          },
          { field: "campaign_id", as: "campaign_id", type: "string" },
        ],
      ),

      // 3. Source budget attribution runCode
      n("3", "runCode", "Attribute Source and Budget", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Attribute lead to campaign source and calculate cost per lead
const mdfBudget = parseFloat(vars.mdf_budget) || 0;
const campaignType = (vars.campaign_type || "").toLowerCase();
const partnerCompany = vars.partner_company || "unknown";
let estimatedLeadsFromCampaign = 1;
if (campaignType.includes("webinar")) estimatedLeadsFromCampaign = 50;
else if (campaignType.includes("event")) estimatedLeadsFromCampaign = 100;
else if (campaignType.includes("content")) estimatedLeadsFromCampaign = 30;
else if (campaignType.includes("ad")) estimatedLeadsFromCampaign = 75;
const costPerLead = mdfBudget > 0 ? Math.round((mdfBudget / estimatedLeadsFromCampaign) * 100) / 100 : 0;
const attribution = "partner_co_marketing";
const revenueShare = campaignType.includes("event") ? "60/40" : "50/50";
return {
  attribution_source: attribution,
  cost_per_lead: costPerLead,
  revenue_share_split: revenueShare,
  campaign_category: campaignType || "general",
  partner_attribution: partnerCompany,
  budget_utilization: mdfBudget > 0 ? "active" : "no_mdf"
};`,
          outputVariables: [
            { name: "attribution_source", type: "string" },
            { name: "cost_per_lead", type: "number" },
            { name: "revenue_share_split", type: "string" },
            { name: "campaign_category", type: "string" },
            { name: "partner_attribution", type: "string" },
            { name: "budget_utilization", type: "string" },
          ],
        },
      }),

      // 4. Lead enrichment Clearbit API
      n("4", "hitApi", "Enrich Lead via Clearbit", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/people/find?email={lead_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name.fullName",
              customName: "enriched_name",
              type: "string",
            },
            { name: "title", customName: "enriched_title", type: "string" },
            {
              name: "company.name",
              customName: "enriched_company",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "company_employees",
              type: "string",
            },
            {
              name: "company.category.industry",
              customName: "company_industry",
              type: "string",
            },
          ],
        },
      }),

      // 5. Co-branded touch 1 email
      emailNode(
        "5",
        "Send Co-Branded Touch 1 Email",
        1000,
        "{lead_email}",
        "{lead_name}, thanks for joining our {campaign_name} with {partner_company}",
        "<p>Hi {lead_name},</p><p>Thank you for your interest in our joint {campaign_type} with {partner_company}: <strong>{campaign_name}</strong>.</p><p>We are excited to share how {enriched_company} can benefit from the combined expertise of our teams. Here is what we have put together for you:</p><ul><li>Key insights from the {campaign_name} campaign</li><li>How companies in {company_industry} are leveraging our partnership</li><li>Next steps to explore the solution together</li></ul><p>Our teams at both {partner_company} and our company are available to answer any questions.</p><p>Best,<br/>The Team</p>",
      ),

      // 6. Delay 5 days
      n("6", "delayAction", "Wait 5 Days Before Touch 2", 1240, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 5,
          delayUnit: "days",
          description:
            "Wait 5 days after the first co-branded email before sending the second touch with deeper content",
        },
      }),

      // 7. Co-branded touch 2 email
      emailNode(
        "7",
        "Send Co-Branded Touch 2 Email",
        1480,
        "{lead_email}",
        "{lead_name}, a deeper look at what {partner_company} and our team can do for {enriched_company}",
        "<p>Hi {lead_name},</p><p>Following up on our {campaign_name} campaign — we wanted to share a more detailed look at how our partnership with {partner_company} delivers unique value for {company_industry} companies like {enriched_company}.</p><p>Here is what sets our joint solution apart:</p><ul><li>Integrated workflows that combine both platforms</li><li>Implementation support from certified {partner_company} consultants</li><li>Industry-specific configurations for {company_industry}</li></ul><p>Would you like to see a joint demo? Our teams can walk you through the complete solution in 20 minutes.</p><p><a href='https://calendly.com/your-team/partner-demo?email={lead_email}&partner={partner_company}'>Book a joint demo</a></p><p>Best,<br/>The Team in partnership with {partner_company}</p>",
      ),

      // 8. Partner-facing report Sheets
      sheetsNode(
        "8",
        "Log to Partner-Facing Report",
        1720,
        "CoMarketingReport",
        '[["{campaign_id}","{campaign_name}","{partner_company}","{campaign_category}","{mdf_budget}","{lead_name}","{lead_email}","{enriched_company}","{company_industry}","{cost_per_lead}","{revenue_share_split}","{attribution_source}","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1577. PARTNER OR INTEGRATOR REACTIVATION (8 nodes)
  // ===================================================================

  "Partner or Integrator Reactivation": {
    summary:
      "Runs on a schedule to detect dormant partners, loads partner history from HubSpot, uses AI to select a reactivation strategy, sends a reactivation email, waits 7 days, checks for a response via condition, and alerts the channel manager on Slack if no response.",
    nodes: [
      trigger("Schedule: Weekly dormant partner scan", "schedule", {
        schedule: "Every Monday at 9:00 AM",
        howItStarts:
          "Runs weekly to scan the partner database for channel partners or integrators who have been inactive for 60+ days — no deal registrations, no co-marketing activity, and no portal logins",
        setup:
          "Set HubSpot private app token for partner data queries. Set OpenAI API key for reactivation strategy selection. Configure email for partner outreach. Set Slack webhook for channel manager escalation alerts.",
        inputFields: [],
      }),

      // 2. Dormant partner detector HubSpot
      hubspotGetNode(
        "2",
        "Detect Dormant Partners in HubSpot",
        280,
        "crm/v3/objects/contacts/search?query=partner_status:inactive&properties=email,firstname,lastname,company,partner_tier,partner_status,last_deal_registration_date,last_login_date,total_deals_registered,total_revenue_generated",
        [
          {
            name: "results[0].properties.email",
            customName: "partner_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "partner_first_name",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "partner_last_name",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "partner_company",
            type: "string",
          },
          {
            name: "results[0].properties.partner_tier",
            customName: "partner_tier",
            type: "string",
          },
          {
            name: "results[0].properties.last_deal_registration_date",
            customName: "last_deal_date",
            type: "string",
          },
          {
            name: "results[0].properties.total_deals_registered",
            customName: "total_deals",
            type: "string",
          },
          {
            name: "results[0].properties.total_revenue_generated",
            customName: "total_revenue",
            type: "string",
          },
          {
            name: "total",
            customName: "dormant_partner_count",
            type: "number",
          },
        ],
      ),

      // 3. Partner history loader HubSpot
      hubspotGetNode(
        "3",
        "Load Partner History from HubSpot",
        520,
        "crm/v3/objects/contacts/{partner_email}?idProperty=email&properties=partner_specialization,partner_certifications,partner_region,notes_last_contacted,hs_lead_status,partner_manager",
        [
          {
            name: "properties.partner_specialization",
            customName: "specialization",
            type: "string",
          },
          {
            name: "properties.partner_certifications",
            customName: "certifications",
            type: "string",
          },
          {
            name: "properties.partner_region",
            customName: "region",
            type: "string",
          },
          {
            name: "properties.notes_last_contacted",
            customName: "last_contacted_note",
            type: "string",
          },
          {
            name: "properties.partner_manager",
            customName: "channel_manager",
            type: "string",
          },
        ],
      ),

      // 4. AI reactivation strategy selector
      aiNode(
        "4",
        "AI Select Reactivation Strategy",
        760,
        "You are a channel partner reactivation strategist. Given a dormant partner's history including their tier, specialization, past deal volume, total revenue generated, and region, select the best reactivation approach. Consider offering exclusive deal registration bonuses, new product training, co-marketing MDF funds, or access to a new partner tier. Respond JSON: {reactivation_strategy: string, email_subject: string, email_body_html: string, incentive_offer: string, training_recommendation: string, reactivation_urgency: string, win_back_angle: string, escalation_needed: string}",
        "Partner: {partner_first_name} {partner_last_name} at {partner_company}. Tier: {partner_tier}. Specialization: {specialization}. Region: {region}. Total deals: {total_deals}. Total revenue: ${total_revenue}. Last deal: {last_deal_date}. Certifications: {certifications}. Last contact note: {last_contacted_note}. Select the optimal reactivation strategy.",
        [
          {
            field: "reactivation_strategy",
            as: "reactivation_strategy",
            type: "string",
          },
          {
            field: "email_subject",
            as: "reactivation_subject",
            type: "string",
          },
          { field: "email_body_html", as: "reactivation_body", type: "string" },
          { field: "incentive_offer", as: "incentive_offer", type: "string" },
          {
            field: "training_recommendation",
            as: "training_recommendation",
            type: "string",
          },
          {
            field: "reactivation_urgency",
            as: "reactivation_urgency",
            type: "string",
          },
          { field: "win_back_angle", as: "win_back_angle", type: "string" },
          {
            field: "escalation_needed",
            as: "escalation_needed",
            type: "string",
          },
        ],
      ),

      // 5. Reactivation email
      emailNode(
        "5",
        "Send Reactivation Email",
        1000,
        "{partner_email}",
        "{reactivation_subject}",
        "{reactivation_body}",
      ),

      // 6. Delay 7 days
      n("6", "delayAction", "Wait 7 Days for Response", 1240, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 7,
          delayUnit: "days",
          description:
            "Wait 7 days after the reactivation email to check for partner re-engagement before escalating to the channel manager",
        },
      }),

      // 7. Response monitor condition
      n("7", "condition", "Does Partner Need Escalation?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "escalation_needed", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 8. Manager outreach alert Slack
      slackNode(
        "8",
        "Alert Channel Manager on Slack",
        1720,
        "Dormant Partner Escalation\\n*{partner_first_name} {partner_last_name}* at {partner_company}\\nTier: {partner_tier} | Region: {region}\\nSpecialization: {specialization}\\nTotal Deals: {total_deals} | Revenue: ${total_revenue}\\nLast Deal: {last_deal_date}\\nStrategy Used: {reactivation_strategy}\\nIncentive Offered: {incentive_offer}\\nNo response after 7 days — personal outreach recommended.\\nChannel Manager: {channel_manager}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ===================================================================
  // 1578. PERSONAL CRM CONTACT LOGGER (7 nodes)
  // ===================================================================

  "Personal CRM Contact Logger": {
    summary:
      "Fires when an interaction signal is received via webhook, extracts contact data with AI, classifies the relationship with runCode, checks for duplicates in HubSpot, upserts the contact in HubSpot, logs the interaction in HubSpot, and sends a weekly review digest email.",
    nodes: [
      trigger("Webhook: Interaction signal received", "webhook_app", {
        source: "interaction_tracker",
        event: "contact.interaction_signal",
        howItStarts:
          "Fires when an interaction signal is captured — including email replies, calendar meetings, LinkedIn connections, business card scans, phone calls, or Slack DMs — that should be logged as a relationship touchpoint",
        setup:
          "Configure interaction tracking sources to POST webhook events. Set OpenAI API key for contact data extraction. Provide HubSpot token for CRM operations. Configure email for weekly digest delivery.",
        inputFields: [],
      }),

      // 2. AI extract contact signal
      aiNode(
        "2",
        "AI Extract Contact Signal",
        280,
        "You are a personal CRM interaction parser. Extract the contact name, email, phone, company, job title, the interaction type (email, meeting, call, linkedin, event), interaction date, a brief summary of the interaction, the sentiment (positive, neutral, negative), and any follow-up commitments from the raw webhook payload. Respond JSON: {contact_name: string, contact_email: string, contact_phone: string, contact_company: string, contact_title: string, interaction_type: string, interaction_date: string, interaction_summary: string, sentiment: string, follow_up_needed: string, follow_up_date: string, tags: string}",
        "Raw webhook payload: {webhook_payload}. Extract all contact interaction data regardless of field naming conventions.",
        [
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_phone", as: "contact_phone", type: "string" },
          { field: "contact_company", as: "contact_company", type: "string" },
          { field: "contact_title", as: "contact_title", type: "string" },
          { field: "interaction_type", as: "interaction_type", type: "string" },
          { field: "interaction_date", as: "interaction_date", type: "string" },
          {
            field: "interaction_summary",
            as: "interaction_summary",
            type: "string",
          },
          { field: "sentiment", as: "sentiment", type: "string" },
          { field: "follow_up_needed", as: "follow_up_needed", type: "string" },
          { field: "follow_up_date", as: "follow_up_date", type: "string" },
          { field: "tags", as: "tags", type: "string" },
        ],
      ),

      // 3. Relationship classifier runCode
      n("3", "runCode", "Classify Relationship Strength", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Classify relationship strength based on interaction type and sentiment
const interactionType = (vars.interaction_type || "").toLowerCase();
const sentiment = (vars.sentiment || "").toLowerCase();
const followUpNeeded = vars.follow_up_needed === "yes";
let relationshipScore = 50;
if (interactionType === "meeting") relationshipScore += 20;
else if (interactionType === "call") relationshipScore += 15;
else if (interactionType === "email") relationshipScore += 10;
else if (interactionType === "linkedin") relationshipScore += 8;
else if (interactionType === "event") relationshipScore += 12;
if (sentiment === "positive") relationshipScore += 15;
else if (sentiment === "negative") relationshipScore -= 10;
if (followUpNeeded) relationshipScore += 5;
const strength = relationshipScore >= 80 ? "strong" : relationshipScore >= 60 ? "growing" : relationshipScore >= 40 ? "casual" : "new";
return {
  relationship_score: Math.min(100, relationshipScore),
  relationship_strength: strength,
  contact_priority: relationshipScore >= 70 ? "high" : "normal",
  needs_attention: followUpNeeded ? "yes" : "no"
};`,
          outputVariables: [
            { name: "relationship_score", type: "number" },
            { name: "relationship_strength", type: "string" },
            { name: "contact_priority", type: "string" },
            { name: "needs_attention", type: "string" },
          ],
        },
      }),

      // 4. CRM duplicate checker HubSpot
      hubspotGetNode(
        "4",
        "Check for Duplicate Contact in HubSpot",
        760,
        "crm/v3/objects/contacts/{contact_email}?idProperty=email&properties=firstname,lastname,company,jobtitle,notes_last_contacted,hs_lead_status",
        [
          { name: "id", customName: "existing_contact_id", type: "string" },
          {
            name: "properties.firstname",
            customName: "existing_first_name",
            type: "string",
          },
          {
            name: "properties.lastname",
            customName: "existing_last_name",
            type: "string",
          },
          {
            name: "properties.notes_last_contacted",
            customName: "last_note",
            type: "string",
          },
        ],
      ),

      // 5. CRM contact upsert HubSpot
      hubspotPostNode(
        "5",
        "Upsert Contact in HubSpot",
        1000,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{contact_email}","firstname":"{contact_name}","company":"{contact_company}","jobtitle":"{contact_title}","phone":"{contact_phone}","hs_lead_status":"IN_PROGRESS","relationship_strength":"{relationship_strength}","relationship_score":"{relationship_score}","last_interaction_type":"{interaction_type}","last_interaction_date":"{interaction_date}","contact_tags":"{tags}"}',
          },
        ],
        [
          { name: "id", customName: "hs_contact_id", type: "string" },
          {
            name: "properties.createdate",
            customName: "contact_date",
            type: "string",
          },
        ],
      ),

      // 6. Interaction logger HubSpot
      hubspotPostNode(
        "6",
        "Log Interaction in HubSpot",
        1240,
        "crm/v3/objects/notes",
        [
          {
            key: "properties",
            value:
              '{"hs_note_body":"{interaction_type} on {interaction_date}: {interaction_summary}\\nSentiment: {sentiment} | Strength: {relationship_strength} ({relationship_score})\\nFollow-up: {follow_up_needed} (by {follow_up_date})\\nTags: {tags}","hs_timestamp":"' +
              new Date().toISOString() +
              '"}',
          },
          {
            key: "associations",
            value:
              '[{"to":{"id":"{hs_contact_id}"},"types":[{"associationCategory":"HUBSPOT_DEFINED","associationTypeId":202}]}]',
          },
        ],
        [{ name: "id", customName: "note_id", type: "string" }],
      ),

      // 7. Weekly review digest email
      emailNode(
        "7",
        "Send Weekly Review Digest Email",
        1480,
        "{contact_email}",
        "Interaction logged: {interaction_type} with {contact_name}",
        "<p>Hi there,</p><p>Your personal CRM has been updated with a new interaction:</p><ul><li><strong>Contact:</strong> {contact_name} ({contact_title} at {contact_company})</li><li><strong>Type:</strong> {interaction_type}</li><li><strong>Date:</strong> {interaction_date}</li><li><strong>Summary:</strong> {interaction_summary}</li><li><strong>Sentiment:</strong> {sentiment}</li><li><strong>Relationship Strength:</strong> {relationship_strength} (score: {relationship_score})</li></ul><p><strong>Follow-up needed:</strong> {follow_up_needed}</p><p>Keep building those relationships!</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1579. PIPELINE UPDATE WEBHOOK (7 nodes)
  // ===================================================================

  "Pipeline Update Webhook": {
    summary:
      "Fires when a pipeline change occurs via webhook, extracts change data with AI, classifies the change type with runCode, scores business impact with runCode, sends a Slack alert for material moves, logs analytics to Sheets, and checks for regression via condition.",
    nodes: [
      trigger("Webhook: Pipeline change detected", "webhook_app", {
        source: "hubspot",
        event: "deal.propertyChange",
        howItStarts:
          "Fires when a deal moves stages, changes amount, updates close date, or has any material pipeline change in the CRM — enabling real-time pipeline monitoring and alerting",
        setup:
          "Configure HubSpot webhook for deal property change events. Set OpenAI API key for change data extraction. Set Slack webhook for pipeline alerts. Configure Google Sheets for analytics dashboard. Define material change thresholds.",
        inputFields: [],
      }),

      // 2. AI extract change data
      aiNode(
        "2",
        "AI Extract Pipeline Change Data",
        280,
        "You are a CRM pipeline change parser. Extract the deal name, deal ID, previous stage, new stage, previous amount, new amount, previous close date, new close date, deal owner, company name, change timestamp, and the property that changed from the raw webhook payload. Respond JSON: {deal_name: string, deal_id: string, previous_stage: string, new_stage: string, previous_amount: string, new_amount: string, previous_close_date: string, new_close_date: string, deal_owner: string, company_name: string, change_timestamp: string, changed_property: string, pipeline_name: string}",
        "Raw webhook payload: {webhook_payload}. Extract all pipeline change data regardless of field naming conventions.",
        [
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "previous_stage", as: "previous_stage", type: "string" },
          { field: "new_stage", as: "new_stage", type: "string" },
          { field: "previous_amount", as: "previous_amount", type: "string" },
          { field: "new_amount", as: "new_amount", type: "string" },
          {
            field: "previous_close_date",
            as: "previous_close_date",
            type: "string",
          },
          { field: "new_close_date", as: "new_close_date", type: "string" },
          { field: "deal_owner", as: "deal_owner", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "change_timestamp", as: "change_timestamp", type: "string" },
          { field: "changed_property", as: "changed_property", type: "string" },
          { field: "pipeline_name", as: "pipeline_name", type: "string" },
        ],
      ),

      // 3. Change type classifier runCode
      n("3", "runCode", "Classify Pipeline Change Type", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Classify the type and direction of the pipeline change
const prevStage = (vars.previous_stage || "").toLowerCase();
const newStage = (vars.new_stage || "").toLowerCase();
const prevAmount = parseFloat(vars.previous_amount) || 0;
const newAmount = parseFloat(vars.new_amount) || 0;
const changedProp = (vars.changed_property || "").toLowerCase();
let changeType = "update";
let direction = "neutral";
let isRegression = "false";
if (changedProp.includes("stage") || changedProp.includes("dealstage")) {
  changeType = "stage_change";
  const stageOrder = ["appointmentscheduled","qualifiedtobuy","presentationscheduled","decisionmakerboughtin","contractsent","closedwon","closedlost"];
  const prevIdx = stageOrder.indexOf(prevStage);
  const newIdx = stageOrder.indexOf(newStage);
  if (newIdx > prevIdx) direction = "forward";
  else if (newIdx < prevIdx) { direction = "backward"; isRegression = "true"; }
  if (newStage === "closedwon") changeType = "closed_won";
  if (newStage === "closedlost") changeType = "closed_lost";
} else if (changedProp.includes("amount")) {
  changeType = "amount_change";
  direction = newAmount > prevAmount ? "increase" : newAmount < prevAmount ? "decrease" : "neutral";
  isRegression = newAmount < prevAmount ? "true" : "false";
} else if (changedProp.includes("closedate")) {
  changeType = "date_change";
  const prevDate = new Date(vars.previous_close_date);
  const newDate = new Date(vars.new_close_date);
  direction = newDate > prevDate ? "pushed" : "pulled_in";
  isRegression = newDate > prevDate ? "true" : "false";
}
const amountDelta = newAmount - prevAmount;
return { change_type: changeType, direction: direction, is_regression: isRegression, amount_delta: amountDelta, change_summary: changeType + " (" + direction + ")" };`,
          outputVariables: [
            { name: "change_type", type: "string" },
            { name: "direction", type: "string" },
            { name: "is_regression", type: "string" },
            { name: "amount_delta", type: "number" },
            { name: "change_summary", type: "string" },
          ],
        },
      }),

      // 4. Business impact scorer runCode
      n("4", "runCode", "Score Business Impact", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score the business impact of the pipeline change
const changeType = vars.change_type || "";
const direction = vars.direction || "";
const newAmount = parseFloat(vars.new_amount) || 0;
const amountDelta = parseFloat(vars.amount_delta) || 0;
const isRegression = vars.is_regression === "true";
let impactScore = 0;
if (changeType === "closed_won") impactScore = 100;
else if (changeType === "closed_lost") impactScore = 90;
else if (changeType === "stage_change" && direction === "forward") impactScore = 60;
else if (changeType === "stage_change" && direction === "backward") impactScore = 75;
else if (changeType === "amount_change") impactScore = Math.min(80, Math.abs(amountDelta) / 1000);
else if (changeType === "date_change") impactScore = 50;
if (newAmount > 100000) impactScore = Math.min(100, impactScore + 20);
const severity = impactScore >= 80 ? "critical" : impactScore >= 50 ? "material" : "minor";
const alertNeeded = impactScore >= 40 ? "true" : "false";
return { impact_score: Math.round(impactScore), severity: severity, alert_needed: alertNeeded, impact_reason: changeType + " | delta:" + amountDelta + " | amount:" + newAmount };`,
          outputVariables: [
            { name: "impact_score", type: "number" },
            { name: "severity", type: "string" },
            { name: "alert_needed", type: "string" },
            { name: "impact_reason", type: "string" },
          ],
        },
      }),

      // 5. Slack alert for material moves
      slackNode(
        "5",
        "Send Pipeline Alert to Slack",
        1000,
        "Pipeline Update [{severity}]\\n*{deal_name}* ({company_name}) — ${new_amount}\\nChange: {change_summary}\\nPrev Stage: {previous_stage} -> New Stage: {new_stage}\\nPrev Amount: ${previous_amount} -> New Amount: ${new_amount} (delta: ${amount_delta})\\nClose Date: {previous_close_date} -> {new_close_date}\\nOwner: {deal_owner} | Impact Score: {impact_score}\\nRegression: {is_regression}",
      ),

      // 6. Analytics dashboard Sheets
      sheetsNode(
        "6",
        "Log to Analytics Dashboard",
        1240,
        "PipelineAnalytics",
        '[["{deal_name}","{deal_id}","{company_name}","{change_type}","{direction}","{previous_stage}","{new_stage}","{previous_amount}","{new_amount}","{amount_delta}","{impact_score}","{severity}","{is_regression}","{deal_owner}","{change_timestamp}"]]',
      ),

      // 7. Regression escalation condition
      n("7", "condition", "Is This a Pipeline Regression?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "is_regression", operator: "eq", value: "true" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1580. PRICING-PAGE VISIT ALERT PLUS NURTURE (8 nodes)
  // ===================================================================

  "Pricing-Page Visit Alert Plus Nurture": {
    summary:
      "Fires when a pricing page visit is detected via webhook, extracts visitor data with AI, loads lead context from HubSpot, uses AI to interpret visit intent, routes via sales alert condition, sends a contextual pricing follow-up email, updates the CRM score in HubSpot, and logs the visit to Sheets.",
    nodes: [
      trigger("Webhook: Pricing page visit detected", "webhook_app", {
        source: "website_analytics",
        event: "page.pricing_visited",
        howItStarts:
          "Fires when an identified lead visits the pricing page — indicating strong purchase intent — captured via tracking pixel, Segment, or CMS behavioral tracking",
        setup:
          "Configure website tracking to fire webhook on pricing page visits with visitor identity. Set OpenAI API key for AI extraction and intent interpretation. Provide HubSpot token for lead context and CRM updates. Configure email and Slack for sales alerts. Set Google Sheets for visit logging.",
        inputFields: [],
      }),

      // 2. AI extract visitor data
      aiNode(
        "2",
        "AI Extract Pricing Visitor Data",
        280,
        "You are a website analytics parser. Extract the visitor email, name, pricing page URL, time on page, scroll depth percentage, the pricing tier or plan they focused on, number of visits to pricing page, referrer URL, UTM parameters, and device type from the raw webhook payload. Respond JSON: {visitor_email: string, visitor_name: string, pricing_url: string, time_on_page_seconds: string, scroll_depth_pct: string, pricing_tier_focused: string, visit_count: string, referrer_url: string, utm_source: string, utm_campaign: string, device_type: string, session_id: string}",
        "Raw webhook payload: {webhook_payload}. Extract all pricing page visit data regardless of field naming conventions.",
        [
          { field: "visitor_email", as: "visitor_email", type: "string" },
          { field: "visitor_name", as: "visitor_name", type: "string" },
          { field: "pricing_url", as: "pricing_url", type: "string" },
          {
            field: "time_on_page_seconds",
            as: "time_on_page_seconds",
            type: "string",
          },
          { field: "scroll_depth_pct", as: "scroll_depth_pct", type: "string" },
          {
            field: "pricing_tier_focused",
            as: "pricing_tier_focused",
            type: "string",
          },
          { field: "visit_count", as: "visit_count", type: "string" },
          { field: "referrer_url", as: "referrer_url", type: "string" },
          { field: "utm_source", as: "utm_source", type: "string" },
          { field: "utm_campaign", as: "utm_campaign", type: "string" },
          { field: "device_type", as: "device_type", type: "string" },
          { field: "session_id", as: "session_id", type: "string" },
        ],
      ),

      // 3. Lead context loader HubSpot
      hubspotGetNode(
        "3",
        "Load Lead Context from HubSpot",
        520,
        "crm/v3/objects/contacts/{visitor_email}?idProperty=email&properties=firstname,lastname,company,industry,lifecyclestage,hs_lead_status,jobtitle,num_associated_deals,hs_analytics_num_page_views",
        [
          {
            name: "properties.firstname",
            customName: "first_name",
            type: "string",
          },
          {
            name: "properties.lastname",
            customName: "last_name",
            type: "string",
          },
          {
            name: "properties.company",
            customName: "company_name",
            type: "string",
          },
          {
            name: "properties.industry",
            customName: "industry",
            type: "string",
          },
          {
            name: "properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "properties.jobtitle",
            customName: "job_title",
            type: "string",
          },
          {
            name: "properties.num_associated_deals",
            customName: "num_deals",
            type: "string",
          },
        ],
      ),

      // 4. AI visit intent interpreter
      aiNode(
        "4",
        "AI Interpret Pricing Visit Intent",
        760,
        "You are a B2B purchase intent analyst. Given the visitor's pricing page behavior (time, scroll depth, tier focused on, repeat visits) and their CRM profile (stage, title, industry), determine the intent signal strength, the most likely objection they are evaluating, and craft a personalized follow-up message that addresses their specific situation. Respond JSON: {intent_signal: string, intent_score: string, likely_objection: string, personalized_subject: string, personalized_body_html: string, recommended_action: string, is_sales_ready: string, suggested_tier: string}",
        "Visitor: {first_name} {last_name}, Title: {job_title}, Company: {company_name} ({industry}). Stage: {lifecycle_stage}. Deals: {num_deals}. Pricing behavior: {time_on_page_seconds}s on page, {scroll_depth_pct}% scroll, focused on {pricing_tier_focused}, visit #{visit_count}. Referrer: {referrer_url}. Interpret the purchase intent.",
        [
          { field: "intent_signal", as: "intent_signal", type: "string" },
          { field: "intent_score", as: "intent_score", type: "string" },
          { field: "likely_objection", as: "likely_objection", type: "string" },
          {
            field: "personalized_subject",
            as: "personalized_subject",
            type: "string",
          },
          {
            field: "personalized_body_html",
            as: "personalized_body_html",
            type: "string",
          },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          { field: "is_sales_ready", as: "is_sales_ready", type: "string" },
          { field: "suggested_tier", as: "suggested_tier", type: "string" },
        ],
      ),

      // 5. Sales alert condition
      n("5", "condition", "Is Lead Sales-Ready?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "is_sales_ready", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 6. Contextual pricing follow-up email
      emailNode(
        "6",
        "Send Contextual Pricing Follow-Up Email",
        1240,
        "{visitor_email}",
        "{personalized_subject}",
        "{personalized_body_html}",
      ),

      // 7. CRM score update HubSpot
      hubspotPostNode(
        "7",
        "Update Lead Score in HubSpot",
        1480,
        "crm/v3/objects/contacts/{visitor_email}?idProperty=email",
        [
          {
            key: "properties",
            value:
              '{"hs_lead_status":"OPEN","pricing_page_visits":"{visit_count}","pricing_tier_interest":"{pricing_tier_focused}","intent_signal":"{intent_signal}","intent_score":"{intent_score}","notes_last_contacted":"Pricing page visit #{visit_count}: {time_on_page_seconds}s, {scroll_depth_pct}% scroll, focused on {pricing_tier_focused}. Intent: {intent_signal} ({intent_score}). Objection: {likely_objection}"}',
          },
        ],
        [
          { name: "id", customName: "hs_contact_id", type: "string" },
          {
            name: "properties.hs_lead_status",
            customName: "updated_status",
            type: "string",
          },
        ],
      ),

      // 8. Subsequent action check Sheets
      sheetsNode(
        "8",
        "Log Pricing Visit to Sheets",
        1720,
        "PricingPageVisits",
        '[["{visitor_email}","{first_name} {last_name}","{company_name}","{industry}","{job_title}","{pricing_tier_focused}","{time_on_page_seconds}","{scroll_depth_pct}","{visit_count}","{intent_signal}","{intent_score}","{is_sales_ready}","{recommended_action}","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1581. PROCUREMENT OR LEGAL STAGE REMINDER (8 nodes)
  // ===================================================================

  "Procurement or Legal Stage Reminder": {
    summary:
      "Fires when a deal enters procurement or legal stage via webhook, extracts deal data with AI, loads stakeholder context from HubSpot, sends a proactive resource pack email, alerts internal legal on Slack, waits 7 days, checks for stall via condition, and sends an escalation alert on Slack.",
    nodes: [
      trigger("Webhook: Deal enters procurement or legal", "webhook_app", {
        source: "hubspot",
        event: "deal.stage_change_to_procurement",
        howItStarts:
          "Fires when a deal moves into procurement, legal review, or contract negotiation stage — indicating the deal is nearing close but at risk of stalling in red tape",
        setup:
          "Configure HubSpot webhook for deal stage changes to procurement/legal/contract stages. Set OpenAI API key for AI extraction. Provide HubSpot token for stakeholder data. Configure email for resource delivery. Set Slack webhook for legal and escalation alerts.",
        inputFields: [],
      }),

      // 2. AI extract deal data
      aiNode(
        "2",
        "AI Extract Procurement Deal Data",
        280,
        "You are a CRM deal stage parser. Extract the deal name, deal ID, amount, the previous stage, current stage (procurement/legal/contract), deal owner name and email, primary contact name and email, company name, expected close date, and any blockers or requirements mentioned from the raw webhook payload. Respond JSON: {deal_name: string, deal_id: string, deal_amount: string, previous_stage: string, current_stage: string, deal_owner_name: string, deal_owner_email: string, contact_name: string, contact_email: string, company_name: string, expected_close_date: string, blockers: string, procurement_requirements: string}",
        "Raw webhook payload: {webhook_payload}. Extract all deal procurement/legal stage data regardless of field naming conventions.",
        [
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "string" },
          { field: "previous_stage", as: "previous_stage", type: "string" },
          { field: "current_stage", as: "current_stage", type: "string" },
          { field: "deal_owner_name", as: "deal_owner_name", type: "string" },
          { field: "deal_owner_email", as: "deal_owner_email", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          {
            field: "expected_close_date",
            as: "expected_close_date",
            type: "string",
          },
          { field: "blockers", as: "blockers", type: "string" },
          {
            field: "procurement_requirements",
            as: "procurement_requirements",
            type: "string",
          },
        ],
      ),

      // 3. Deal stakeholder context HubSpot
      hubspotGetNode(
        "3",
        "Load Deal Stakeholder Context from HubSpot",
        520,
        "crm/v3/objects/deals/{deal_id}?properties=dealname,amount,dealstage,closedate,num_associated_contacts,hs_deal_stage_probability,notes_last_contacted,pipeline",
        [
          {
            name: "properties.num_associated_contacts",
            customName: "total_contacts",
            type: "string",
          },
          {
            name: "properties.hs_deal_stage_probability",
            customName: "stage_probability",
            type: "string",
          },
          {
            name: "properties.notes_last_contacted",
            customName: "last_note",
            type: "string",
          },
          {
            name: "properties.pipeline",
            customName: "pipeline_name",
            type: "string",
          },
        ],
      ),

      // 4. Proactive resource pack email
      emailNode(
        "4",
        "Send Proactive Resource Pack Email",
        760,
        "{contact_email}",
        "{contact_name}, resources to streamline {company_name}'s procurement process",
        "<p>Hi {contact_name},</p><p>We know that procurement and legal review can take time, so we have prepared a resource pack to help move things along smoothly for {company_name}:</p><ul><li><strong>Security & Compliance Documentation:</strong> SOC 2 report, GDPR data processing agreement, and penetration test summary</li><li><strong>Vendor Questionnaire:</strong> Pre-filled responses to common procurement questions</li><li><strong>Reference Contacts:</strong> Customers in your industry available for reference calls</li><li><strong>Contract Templates:</strong> Standard MSA and order form templates</li></ul><p>If your procurement or legal team has specific requirements, please share them and we will prioritize getting the right documentation to you.</p><p>We are committed to making this as smooth as possible. Your deal owner, {deal_owner_name}, is available anytime to coordinate.</p><p>Best,<br/>The Team</p>",
      ),

      // 5. Internal legal alert Slack
      slackNode(
        "5",
        "Alert Internal Legal Team on Slack",
        1000,
        "Deal entering {current_stage}\\n*{deal_name}* — {company_name} (${deal_amount})\\nContact: {contact_name} ({contact_email})\\nDeal Owner: {deal_owner_name}\\nExpected Close: {expected_close_date}\\nStage Probability: {stage_probability}%\\nBlockers: {blockers}\\nRequirements: {procurement_requirements}\\nTotal Contacts: {total_contacts}\\nProactive resource pack sent to prospect. Legal team to prepare required documentation.",
      ),

      // 6. Delay 7 days
      n("6", "delayAction", "Wait 7 Days for Progress", 1240, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 7,
          delayUnit: "days",
          description:
            "Wait 7 days to check if procurement/legal review is progressing or has stalled",
        },
      }),

      // 7. Stall detection condition
      n("7", "condition", "Has Deal Stalled in Procurement?", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "current_stage",
              operator: "eq",
              value: "contractsent",
            },
          ],
        },
      }),

      // 8. Escalation alert Slack
      slackNode(
        "8",
        "Escalation Alert for Stalled Deal",
        1720,
        "PROCUREMENT STALL ALERT\\n*{deal_name}* — {company_name} (${deal_amount})\\nStage: {current_stage} for 7+ days\\nDeal Owner: {deal_owner_name} ({deal_owner_email})\\nContact: {contact_name}\\nExpected Close: {expected_close_date}\\nBlockers: {blockers}\\nLast Note: {last_note}\\nAction: Schedule exec-level check-in to unblock procurement process.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1582. PROCUREMENT-DELAY NURTURE (7 nodes)
  // ===================================================================

  "Procurement-Delay Nurture": {
    summary:
      "Fires when a procurement delay is flagged via webhook, extracts delay data with AI, loads champion deal context from HubSpot, uses AI to select value-add content, sends a value-first check-in email, waits 7 days, then sends a champion advocacy email.",
    nodes: [
      trigger("Webhook: Procurement delay flagged", "webhook_app", {
        source: "hubspot",
        event: "deal.procurement_delayed",
        howItStarts:
          "Fires when a deal in procurement or legal review is flagged as delayed — either by automatic stage-age detection or manual rep flagging — indicating the deal needs a nurture touch to maintain momentum",
        setup:
          "Configure HubSpot webhook or automation trigger for procurement delay flags. Set OpenAI API key for AI extraction and content selection. Provide HubSpot token for deal context loading. Configure email for value-add nurture sequences.",
        inputFields: [],
      }),

      // 2. AI extract delay data
      aiNode(
        "2",
        "AI Extract Procurement Delay Data",
        280,
        "You are a deal procurement delay parser. Extract the deal name, champion contact name and email, company name, deal amount, current stage, days in current stage, the delay reason (if known), deal owner, expected close date, and any procurement contact from the raw webhook payload. Respond JSON: {deal_name: string, champion_name: string, champion_email: string, company_name: string, deal_amount: string, current_stage: string, days_in_stage: string, delay_reason: string, deal_owner: string, expected_close_date: string, procurement_contact: string, product_name: string}",
        "Raw webhook payload: {webhook_payload}. Extract all procurement delay data regardless of field naming conventions.",
        [
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "champion_name", as: "champion_name", type: "string" },
          { field: "champion_email", as: "champion_email", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "string" },
          { field: "current_stage", as: "current_stage", type: "string" },
          { field: "days_in_stage", as: "days_in_stage", type: "string" },
          { field: "delay_reason", as: "delay_reason", type: "string" },
          { field: "deal_owner", as: "deal_owner", type: "string" },
          {
            field: "expected_close_date",
            as: "expected_close_date",
            type: "string",
          },
          {
            field: "procurement_contact",
            as: "procurement_contact",
            type: "string",
          },
          { field: "product_name", as: "product_name", type: "string" },
        ],
      ),

      // 3. Champion deal context HubSpot
      hubspotGetNode(
        "3",
        "Load Champion Deal Context from HubSpot",
        520,
        "crm/v3/objects/contacts/{champion_email}?idProperty=email&properties=firstname,lastname,company,jobtitle,industry,lifecyclestage,notes_last_contacted",
        [
          {
            name: "properties.firstname",
            customName: "first_name",
            type: "string",
          },
          {
            name: "properties.lastname",
            customName: "last_name",
            type: "string",
          },
          {
            name: "properties.jobtitle",
            customName: "job_title",
            type: "string",
          },
          {
            name: "properties.industry",
            customName: "industry",
            type: "string",
          },
          {
            name: "properties.notes_last_contacted",
            customName: "last_note",
            type: "string",
          },
        ],
      ),

      // 4. AI value-add content selector
      aiNode(
        "4",
        "AI Select Value-Add Content",
        760,
        "You are a sales enablement strategist specializing in keeping deals alive during procurement delays. Given the champion's profile, the deal context, and the delay reason, select value-add content that keeps the champion engaged and provides internal ammunition for pushing the deal through procurement. Choose content that reinforces the business case without appearing pushy. Respond JSON: {value_content_title: string, value_content_url: string, industry_insight: string, roi_reminder: string, internal_champion_tip: string, check_in_tone: string, advocacy_angle: string, urgency_hook: string}",
        "Champion: {first_name} {last_name}, Title: {job_title}, Industry: {industry}. Deal: {deal_name} (${deal_amount}), Stage: {current_stage}, Days in stage: {days_in_stage}. Delay reason: {delay_reason}. Product: {product_name}. Select value-add content to maintain momentum.",
        [
          {
            field: "value_content_title",
            as: "value_content_title",
            type: "string",
          },
          {
            field: "value_content_url",
            as: "value_content_url",
            type: "string",
          },
          { field: "industry_insight", as: "industry_insight", type: "string" },
          { field: "roi_reminder", as: "roi_reminder", type: "string" },
          {
            field: "internal_champion_tip",
            as: "internal_champion_tip",
            type: "string",
          },
          { field: "check_in_tone", as: "check_in_tone", type: "string" },
          { field: "advocacy_angle", as: "advocacy_angle", type: "string" },
          { field: "urgency_hook", as: "urgency_hook", type: "string" },
        ],
      ),

      // 5. Value-first check-in email
      emailNode(
        "5",
        "Send Value-First Check-In Email",
        1000,
        "{champion_email}",
        "{first_name}, thought you would find this useful while things are in motion",
        "<p>Hi {first_name},</p><p>We know procurement timelines can stretch, so rather than just checking in on the deal, we wanted to share something genuinely useful:</p><p><strong><a href='{value_content_url}'>{value_content_title}</a></strong></p><p>{industry_insight}</p><p>As a quick reminder of the business case: {roi_reminder}</p><p>No pressure at all — just wanted to make sure you have fresh ammunition if anyone asks about the value of {product_name}.</p><p>Let us know if there is anything we can do to help move things along on your end.</p><p>Best,<br/>The Team</p>",
      ),

      // 6. Delay 7 days
      n("6", "delayAction", "Wait 7 Days Before Advocacy Touch", 1240, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 7,
          delayUnit: "days",
          description:
            "Wait 7 days after the value-first email before sending a champion advocacy touch",
        },
      }),

      // 7. Champion advocacy email
      emailNode(
        "7",
        "Send Champion Advocacy Email",
        1480,
        "{champion_email}",
        "{first_name}, a few talking points for your internal team",
        "<p>Hi {first_name},</p><p>We put together some talking points that might help if you need to advocate for {product_name} internally:</p><p>{advocacy_angle}</p><p><strong>Key points for your team:</strong></p><ul><li>{roi_reminder}</li><li>{industry_insight}</li><li>{urgency_hook}</li></ul><p><strong>Pro tip:</strong> {internal_champion_tip}</p><p>We are fully committed to supporting you through this process. If it would help to have our team join a call with your procurement or legal team, we are happy to do that.</p><p>Reach out anytime — we are here to help.</p><p>Best,<br/>The Team</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1583. PRODUCT ANNOUNCEMENT NURTURE FOR LEADS (8 nodes)
  // ===================================================================

  "Product Announcement Nurture for Leads": {
    summary:
      "Fires when a product release event occurs via webhook, extracts release data with AI, segments leads from HubSpot, uses AI to personalize the announcement, sends an announcement email, waits 3 days, checks feature interest via condition, and sends a sales briefing to Slack.",
    nodes: [
      trigger("Webhook: Product release event", "webhook_app", {
        source: "product_management",
        event: "release.published",
        howItStarts:
          "Fires when a new product feature, version, or capability is released — triggering personalized announcement sequences to leads who would benefit from the new functionality",
        setup:
          "Configure product management tool webhook for release events. Set OpenAI API key for AI extraction and personalization. Provide HubSpot token for lead segmentation. Configure email for announcement delivery. Set Slack webhook for sales team briefing.",
        inputFields: [],
      }),

      // 2. AI extract release data
      aiNode(
        "2",
        "AI Extract Release Data",
        280,
        "You are a product release announcement parser. Extract the feature name, feature description, release version, target personas, use cases, competitive advantage, pricing impact, availability date, documentation URL, and the product manager contact from the raw webhook payload. Respond JSON: {feature_name: string, feature_description: string, release_version: string, target_personas: string, use_cases: string, competitive_advantage: string, pricing_impact: string, availability_date: string, docs_url: string, pm_name: string, pm_email: string, release_notes_url: string}",
        "Raw webhook payload: {webhook_payload}. Extract all product release data regardless of field naming conventions.",
        [
          { field: "feature_name", as: "feature_name", type: "string" },
          {
            field: "feature_description",
            as: "feature_description",
            type: "string",
          },
          { field: "release_version", as: "release_version", type: "string" },
          { field: "target_personas", as: "target_personas", type: "string" },
          { field: "use_cases", as: "use_cases", type: "string" },
          {
            field: "competitive_advantage",
            as: "competitive_advantage",
            type: "string",
          },
          { field: "pricing_impact", as: "pricing_impact", type: "string" },
          {
            field: "availability_date",
            as: "availability_date",
            type: "string",
          },
          { field: "docs_url", as: "docs_url", type: "string" },
          { field: "pm_name", as: "pm_name", type: "string" },
          { field: "pm_email", as: "pm_email", type: "string" },
          {
            field: "release_notes_url",
            as: "release_notes_url",
            type: "string",
          },
        ],
      ),

      // 3. Lead segmenter HubSpot
      hubspotGetNode(
        "3",
        "Segment Target Leads from HubSpot",
        520,
        "crm/v3/objects/contacts/search?query=lifecyclestage:lead&properties=email,firstname,lastname,company,industry,jobtitle,lifecyclestage,hs_lead_status",
        [
          {
            name: "results[0].properties.email",
            customName: "lead_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "lead_first_name",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "lead_last_name",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "lead_company",
            type: "string",
          },
          {
            name: "results[0].properties.industry",
            customName: "lead_industry",
            type: "string",
          },
          {
            name: "results[0].properties.jobtitle",
            customName: "lead_title",
            type: "string",
          },
          { name: "total", customName: "total_leads_matched", type: "number" },
        ],
      ),

      // 4. AI announcement personalizer
      aiNode(
        "4",
        "AI Personalize Announcement",
        760,
        "You are a product marketing writer. Given a new feature release and a lead's profile, craft a personalized announcement that highlights how this specific feature solves problems for their industry and role. Make it exciting but not hypey. Focus on concrete use cases and outcomes. Respond JSON: {personalized_subject: string, personalized_body_html: string, key_benefit_for_role: string, industry_use_case: string, cta_text: string, feature_relevance_score: string}",
        "Feature: {feature_name} — {feature_description}. Use cases: {use_cases}. Competitive advantage: {competitive_advantage}. Lead: {lead_first_name} {lead_last_name}, Title: {lead_title}, Company: {lead_company} ({lead_industry}). Personalize the announcement for maximum relevance.",
        [
          {
            field: "personalized_subject",
            as: "announcement_subject",
            type: "string",
          },
          {
            field: "personalized_body_html",
            as: "announcement_body",
            type: "string",
          },
          { field: "key_benefit_for_role", as: "key_benefit", type: "string" },
          {
            field: "industry_use_case",
            as: "industry_use_case",
            type: "string",
          },
          { field: "cta_text", as: "cta_text", type: "string" },
          {
            field: "feature_relevance_score",
            as: "relevance_score",
            type: "string",
          },
        ],
      ),

      // 5. Announcement email
      emailNode(
        "5",
        "Send Product Announcement Email",
        1000,
        "{lead_email}",
        "{announcement_subject}",
        "{announcement_body}",
      ),

      // 6. Delay 3 days
      n("6", "delayAction", "Wait 3 Days for Engagement", 1240, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 3,
          delayUnit: "days",
          description:
            "Wait 3 days after the announcement email to assess engagement before routing to sales",
        },
      }),

      // 7. Feature interest tracker condition
      n("7", "condition", "Is Feature Highly Relevant?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "relevance_score", operator: "gte", value: "8" },
          ],
        },
      }),

      // 8. Sales briefing Slack
      slackNode(
        "8",
        "Send Sales Briefing to Slack",
        1720,
        "Product Announcement Lead Alert\\nNew feature *{feature_name}* ({release_version}) announced\\nLead: *{lead_first_name} {lead_last_name}* — {lead_title} at {lead_company} ({lead_industry})\\nRelevance Score: {relevance_score}/10\\nKey Benefit: {key_benefit}\\nIndustry Use Case: {industry_use_case}\\nTotal Matched Leads: {total_leads_matched}\\nAvailability: {availability_date}\\nDocs: {docs_url}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ===================================================================
  // 1584. PRODUCT-QUALIFIED LEAD NURTURE (9 nodes)
  // ===================================================================

  "Product-Qualified Lead Nurture": {
    summary:
      "Fires when PQL behavior is detected via webhook, extracts usage data with AI, loads usage analytics from API, uses AI to score and personalize, routes via PQL heat condition, sends a usage celebration email, triggers in-app upgrade messaging via API, sends a peer proof email, and records the PQL in HubSpot CRM.",
    nodes: [
      trigger("Webhook: PQL behavior detected", "webhook_app", {
        source: "product_analytics",
        event: "user.pql_threshold_reached",
        howItStarts:
          "Fires when a free or trial user crosses a product-qualified lead threshold — such as using a key feature N times, inviting team members, exceeding usage limits, or hitting activation milestones",
        setup:
          "Configure product analytics webhook for PQL threshold events. Set OpenAI API key for AI scoring and personalization. Provide usage analytics API endpoint. Configure email for outreach. Set HubSpot token for CRM recording.",
        inputFields: [],
      }),

      // 2. AI extract usage data
      aiNode(
        "2",
        "AI Extract PQL Usage Data",
        280,
        "You are a product analytics parser. Extract the user email, name, account ID, plan type (free/trial), the PQL trigger event, feature usage counts, team size, days since signup, activation score, and any usage limits they are approaching from the raw webhook payload. Respond JSON: {user_email: string, user_name: string, account_id: string, plan_type: string, pql_trigger: string, feature_usage_count: string, team_size: string, days_since_signup: string, activation_score: string, usage_limit_pct: string, top_feature_used: string, company_name: string}",
        "Raw webhook payload: {webhook_payload}. Extract all PQL behavior data regardless of field naming conventions.",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "account_id", as: "account_id", type: "string" },
          { field: "plan_type", as: "plan_type", type: "string" },
          { field: "pql_trigger", as: "pql_trigger", type: "string" },
          {
            field: "feature_usage_count",
            as: "feature_usage_count",
            type: "string",
          },
          { field: "team_size", as: "team_size", type: "string" },
          {
            field: "days_since_signup",
            as: "days_since_signup",
            type: "string",
          },
          { field: "activation_score", as: "activation_score", type: "string" },
          { field: "usage_limit_pct", as: "usage_limit_pct", type: "string" },
          { field: "top_feature_used", as: "top_feature_used", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
        ],
      ),

      // 3. Usage analytics loader API
      n("3", "hitApi", "Load Detailed Usage Analytics", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.your-product.com/v1/accounts/{account_id}/usage?period=30d",
          headers: [
            { key: "Authorization", value: "Bearer {product_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "total_sessions",
              customName: "total_sessions",
              type: "number",
            },
            {
              name: "avg_session_duration",
              customName: "avg_session_minutes",
              type: "string",
            },
            {
              name: "features_adopted",
              customName: "features_adopted",
              type: "string",
            },
            {
              name: "integrations_connected",
              customName: "integrations_connected",
              type: "string",
            },
            {
              name: "workspace_members",
              customName: "workspace_members",
              type: "number",
            },
            {
              name: "api_calls_count",
              customName: "api_calls",
              type: "number",
            },
          ],
        },
      }),

      // 4. AI score personalizer
      aiNode(
        "4",
        "AI Score and Personalize PQL Outreach",
        760,
        "You are a product-led growth specialist. Given the user's product usage data, PQL trigger, activation score, and team size, calculate a heat score (hot/warm/cool), determine the best upgrade path, and craft personalized messaging that celebrates their usage while naturally introducing the upgrade opportunity. Respond JSON: {heat_score: string, recommended_plan: string, upgrade_reason: string, celebration_message: string, upgrade_benefit: string, peer_proof_stat: string, urgency_factor: string, in_app_message: string}",
        "User: {user_name} at {company_name}. Plan: {plan_type}. PQL trigger: {pql_trigger}. Feature usage: {feature_usage_count}. Top feature: {top_feature_used}. Team: {team_size} members ({workspace_members} in workspace). Days since signup: {days_since_signup}. Activation: {activation_score}. Usage limit: {usage_limit_pct}%. Sessions: {total_sessions}. Features adopted: {features_adopted}. Integrations: {integrations_connected}. Score and personalize the upgrade outreach.",
        [
          { field: "heat_score", as: "heat_score", type: "string" },
          { field: "recommended_plan", as: "recommended_plan", type: "string" },
          { field: "upgrade_reason", as: "upgrade_reason", type: "string" },
          {
            field: "celebration_message",
            as: "celebration_message",
            type: "string",
          },
          { field: "upgrade_benefit", as: "upgrade_benefit", type: "string" },
          { field: "peer_proof_stat", as: "peer_proof_stat", type: "string" },
          { field: "urgency_factor", as: "urgency_factor", type: "string" },
          { field: "in_app_message", as: "in_app_message", type: "string" },
        ],
      ),

      // 5. PQL heat router condition
      n("5", "condition", "Is PQL Hot?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "heat_score", operator: "eq", value: "hot" },
          ],
        },
      }),

      // 6. Usage celebration email
      emailNode(
        "6",
        "Send Usage Celebration Email",
        1240,
        "{user_email}",
        "{user_name}, you are on fire with {top_feature_used}!",
        "<p>Hi {user_name},</p><p>{celebration_message}</p><p>Here is a snapshot of what you have accomplished:</p><ul><li><strong>{feature_usage_count}</strong> uses of {top_feature_used}</li><li><strong>{total_sessions}</strong> sessions in the last 30 days</li><li><strong>{workspace_members}</strong> team members active in your workspace</li><li><strong>{features_adopted}</strong> features adopted</li></ul><p>{upgrade_reason}</p><p>With the <strong>{recommended_plan}</strong> plan, you would get: {upgrade_benefit}</p><p>{urgency_factor}</p><p><a href='https://your-product.com/upgrade?account={account_id}&plan={recommended_plan}'>Explore {recommended_plan}</a></p><p>Best,<br/>The Team</p>",
      ),

      // 7. In-app upgrade messaging API
      n("7", "hitApi", "Trigger In-App Upgrade Message", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.your-product.com/v1/accounts/{account_id}/in-app-messages",
          headers: [
            { key: "Authorization", value: "Bearer {product_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "message", value: "{in_app_message}" },
            { key: "type", value: "upgrade_prompt" },
            { key: "target_plan", value: "{recommended_plan}" },
            { key: "urgency", value: "{heat_score}" },
          ],
          variables: [
            { name: "id", customName: "in_app_message_id", type: "string" },
            { name: "status", customName: "message_status", type: "string" },
          ],
        },
      }),

      // 8. Peer proof email
      emailNode(
        "8",
        "Send Peer Proof Email",
        1720,
        "{user_email}",
        "{user_name}, see what teams like yours unlocked with {recommended_plan}",
        "<p>Hi {user_name},</p><p>Teams similar to {company_name} that upgraded to {recommended_plan} saw incredible results:</p><p><strong>{peer_proof_stat}</strong></p><p>You are already using {top_feature_used} heavily — imagine what you could do with the full power of {recommended_plan}.</p><p>{upgrade_benefit}</p><p>Ready to take the next step? <a href='https://your-product.com/upgrade?account={account_id}&plan={recommended_plan}'>Upgrade now</a> or <a href='https://calendly.com/your-team/upgrade-call?email={user_email}'>schedule a call</a> to discuss your options.</p><p>Best,<br/>The Team</p>",
      ),

      // 9. CRM PQL record HubSpot
      hubspotPostNode(
        "9",
        "Record PQL in HubSpot",
        1960,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{user_email}","firstname":"{user_name}","company":"{company_name}","hs_lead_status":"OPEN","lifecyclestage":"salesqualifiedlead","pql_trigger":"{pql_trigger}","pql_heat_score":"{heat_score}","recommended_plan":"{recommended_plan}","feature_usage":"{feature_usage_count}","activation_score":"{activation_score}","team_size":"{team_size}","plan_type":"{plan_type}","days_since_signup":"{days_since_signup}"}',
          },
        ],
        [
          { name: "id", customName: "hs_contact_id", type: "string" },
          {
            name: "properties.lifecyclestage",
            customName: "updated_stage",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1585. PRODUCT-USAGE DROP TO CS SAVE PLAY (9 nodes)
  // ===================================================================

  "Product-Usage Drop to CS Save Play": {
    summary:
      "Runs daily on schedule, collects usage metrics from API, analyzes trends with runCode, assesses churn risk with AI, routes by severity via condition, alerts CSM on Slack, sends a proactive check-in email, triggers in-app re-engagement via API, and logs risk to Sheets dashboard.",
    nodes: [
      trigger("Schedule: Daily usage drop scan", "schedule", {
        schedule: "Every day at 7:00 AM",
        howItStarts:
          "Runs daily to scan product usage metrics for accounts showing declining engagement — including login frequency drops, feature usage decreases, or API call reductions that signal churn risk",
        setup:
          "Set product API credentials for usage metrics collection. Set OpenAI API key for churn risk assessment. Configure Slack webhook for CSM alerts. Set email credentials for proactive outreach. Provide Google Sheets for risk dashboard logging.",
        inputFields: [],
      }),

      // 2. Usage metrics collector API
      n("2", "hitApi", "Collect Usage Metrics from Product API", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.your-product.com/v1/analytics/usage-drops?threshold=30&period=7d",
          headers: [
            { key: "Authorization", value: "Bearer {product_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "accounts[0].account_id",
              customName: "account_id",
              type: "string",
            },
            {
              name: "accounts[0].company_name",
              customName: "company_name",
              type: "string",
            },
            {
              name: "accounts[0].primary_contact_email",
              customName: "contact_email",
              type: "string",
            },
            {
              name: "accounts[0].primary_contact_name",
              customName: "contact_name",
              type: "string",
            },
            {
              name: "accounts[0].usage_drop_pct",
              customName: "usage_drop_pct",
              type: "string",
            },
            {
              name: "accounts[0].current_plan",
              customName: "current_plan",
              type: "string",
            },
            {
              name: "accounts[0].mrr",
              customName: "monthly_revenue",
              type: "string",
            },
            {
              name: "accounts[0].days_to_renewal",
              customName: "days_to_renewal",
              type: "string",
            },
            {
              name: "total_at_risk",
              customName: "total_at_risk",
              type: "number",
            },
          ],
        },
      }),

      // 3. Trend analyzer runCode
      n("3", "runCode", "Analyze Usage Trends", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze usage drop severity and calculate risk indicators
const dropPct = parseFloat(vars.usage_drop_pct) || 0;
const daysToRenewal = parseInt(vars.days_to_renewal) || 365;
const mrr = parseFloat(vars.monthly_revenue) || 0;
let severity = "low";
let churnProbability = 0;
if (dropPct >= 70) { severity = "critical"; churnProbability = 85; }
else if (dropPct >= 50) { severity = "high"; churnProbability = 60; }
else if (dropPct >= 30) { severity = "medium"; churnProbability = 35; }
else { severity = "low"; churnProbability = 15; }
if (daysToRenewal <= 30) churnProbability = Math.min(100, churnProbability + 20);
else if (daysToRenewal <= 60) churnProbability = Math.min(100, churnProbability + 10);
const revenueAtRisk = Math.round(mrr * 12 * (churnProbability / 100));
const needsImmediate = severity === "critical" || (severity === "high" && daysToRenewal <= 60);
return {
  severity: severity,
  churn_probability: churnProbability,
  revenue_at_risk: revenueAtRisk,
  needs_immediate_action: needsImmediate ? "true" : "false",
  risk_summary: severity + " risk: " + dropPct + "% drop, " + daysToRenewal + " days to renewal, $" + mrr + "/mo"
};`,
          outputVariables: [
            { name: "severity", type: "string" },
            { name: "churn_probability", type: "number" },
            { name: "revenue_at_risk", type: "number" },
            { name: "needs_immediate_action", type: "string" },
            { name: "risk_summary", type: "string" },
          ],
        },
      }),

      // 4. AI churn risk assessor
      aiNode(
        "4",
        "AI Assess Churn Risk and Plan Response",
        760,
        "You are a customer success churn prevention strategist. Given the usage drop data, severity, churn probability, and account context, determine the most likely reason for disengagement and craft a save play strategy. Include a personalized check-in email, an in-app re-engagement message, and talking points for the CSM. Respond JSON: {likely_churn_driver: string, intervention_strategy: string, check_in_subject: string, check_in_body_html: string, in_app_reengage_message: string, csm_talking_points: string, recommended_offer: string, save_play_urgency: string}",
        "Account: {company_name} ({account_id}). Contact: {contact_name} ({contact_email}). Plan: {current_plan}. MRR: ${monthly_revenue}. Usage drop: {usage_drop_pct}%. Severity: {severity}. Churn probability: {churn_probability}%. Days to renewal: {days_to_renewal}. Revenue at risk: ${revenue_at_risk}/yr. Craft a save play.",
        [
          { field: "likely_churn_driver", as: "churn_driver", type: "string" },
          {
            field: "intervention_strategy",
            as: "intervention_strategy",
            type: "string",
          },
          { field: "check_in_subject", as: "check_in_subject", type: "string" },
          { field: "check_in_body_html", as: "check_in_body", type: "string" },
          {
            field: "in_app_reengage_message",
            as: "in_app_message",
            type: "string",
          },
          {
            field: "csm_talking_points",
            as: "csm_talking_points",
            type: "string",
          },
          {
            field: "recommended_offer",
            as: "recommended_offer",
            type: "string",
          },
          {
            field: "save_play_urgency",
            as: "save_play_urgency",
            type: "string",
          },
        ],
      ),

      // 5. Severity router condition
      n("5", "condition", "Is Immediate Action Needed?", 1000, {
        conditionDetails: {
          conditions: [
            {
              variable: "needs_immediate_action",
              operator: "eq",
              value: "true",
            },
          ],
        },
      }),

      // 6. CSM alert Slack
      slackNode(
        "6",
        "Alert CSM on Slack",
        1240,
        "CHURN RISK [{severity}] — {company_name}\\nContact: {contact_name} ({contact_email})\\nPlan: {current_plan} | MRR: ${monthly_revenue}\\nUsage Drop: {usage_drop_pct}% | Churn Probability: {churn_probability}%\\nRevenue at Risk: ${revenue_at_risk}/yr\\nDays to Renewal: {days_to_renewal}\\nLikely Driver: {churn_driver}\\nStrategy: {intervention_strategy}\\nOffer: {recommended_offer}\\nTalking Points: {csm_talking_points}",
      ),

      // 7. Proactive check-in email
      emailNode(
        "7",
        "Send Proactive Check-In Email",
        1480,
        "{contact_email}",
        "{check_in_subject}",
        "{check_in_body}",
      ),

      // 8. In-app re-engagement API
      n("8", "hitApi", "Trigger In-App Re-Engagement", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.your-product.com/v1/accounts/{account_id}/in-app-messages",
          headers: [
            { key: "Authorization", value: "Bearer {product_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "message", value: "{in_app_message}" },
            { key: "type", value: "re_engagement" },
            { key: "urgency", value: "{save_play_urgency}" },
          ],
          variables: [
            { name: "id", customName: "reengage_message_id", type: "string" },
            { name: "status", customName: "reengage_status", type: "string" },
          ],
        },
      }),

      // 9. Risk dashboard Sheets
      sheetsNode(
        "9",
        "Log Risk to Dashboard",
        1960,
        "ChurnRiskDashboard",
        '[["{company_name}","{account_id}","{contact_name}","{contact_email}","{current_plan}","{monthly_revenue}","{usage_drop_pct}","{severity}","{churn_probability}","{revenue_at_risk}","{days_to_renewal}","{churn_driver}","{intervention_strategy}","{recommended_offer}","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1586. PRODUCT-USAGE SPIKE TO SALES OUTREACH (8 nodes)
  // ===================================================================

  "Product-Usage Spike to Sales Outreach": {
    summary:
      "Fires when a usage spike is detected via webhook, extracts spike data with AI, analyzes the spike cause with runCode, loads expansion context from HubSpot, uses AI to assess the opportunity, sends a value email, alerts the AE on Slack, and creates an expansion opportunity in HubSpot.",
    nodes: [
      trigger("Webhook: Product usage spike detected", "webhook_app", {
        source: "product_analytics",
        event: "account.usage_spike",
        howItStarts:
          "Fires when an account experiences a significant spike in product usage — such as a 3x increase in API calls, doubled login frequency, or new feature adoption surge — indicating potential expansion opportunity",
        setup:
          "Configure product analytics webhook for usage spike events. Set OpenAI API key for AI extraction and opportunity assessment. Provide HubSpot token for expansion context and opportunity creation. Configure email for outreach and Slack for AE alerts.",
        inputFields: [],
      }),

      // 2. AI extract spike data
      aiNode(
        "2",
        "AI Extract Usage Spike Data",
        280,
        "You are a product usage analytics parser. Extract the account ID, company name, primary contact name and email, the spike metric (API calls, logins, feature usage), spike magnitude, baseline usage, current usage, the time period, plan type, and any new features being used from the raw webhook payload. Respond JSON: {account_id: string, company_name: string, contact_name: string, contact_email: string, spike_metric: string, spike_magnitude: string, baseline_usage: string, current_usage: string, spike_period: string, plan_type: string, new_features_used: string, current_mrr: string}",
        "Raw webhook payload: {webhook_payload}. Extract all usage spike data regardless of field naming conventions.",
        [
          { field: "account_id", as: "account_id", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "spike_metric", as: "spike_metric", type: "string" },
          { field: "spike_magnitude", as: "spike_magnitude", type: "string" },
          { field: "baseline_usage", as: "baseline_usage", type: "string" },
          { field: "current_usage", as: "current_usage", type: "string" },
          { field: "spike_period", as: "spike_period", type: "string" },
          { field: "plan_type", as: "plan_type", type: "string" },
          {
            field: "new_features_used",
            as: "new_features_used",
            type: "string",
          },
          { field: "current_mrr", as: "current_mrr", type: "string" },
        ],
      ),

      // 3. Spike cause analyzer runCode
      n("3", "runCode", "Analyze Spike Cause and Potential", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze usage spike patterns to determine expansion potential
const magnitude = parseFloat(vars.spike_magnitude) || 1;
const baseline = parseFloat(vars.baseline_usage) || 0;
const current = parseFloat(vars.current_usage) || 0;
const mrr = parseFloat(vars.current_mrr) || 0;
const newFeatures = (vars.new_features_used || "").split(",").filter(Boolean).length;
let expansionScore = 0;
if (magnitude >= 5) expansionScore += 40;
else if (magnitude >= 3) expansionScore += 30;
else if (magnitude >= 2) expansionScore += 20;
else expansionScore += 10;
if (newFeatures >= 3) expansionScore += 25;
else if (newFeatures >= 1) expansionScore += 15;
if (mrr > 1000) expansionScore += 15;
const potentialExpansion = Math.round(mrr * (magnitude - 1) * 0.5);
const expansionTier = expansionScore >= 60 ? "high" : expansionScore >= 35 ? "medium" : "low";
return {
  expansion_score: Math.min(100, expansionScore),
  expansion_tier: expansionTier,
  potential_expansion_mrr: potentialExpansion,
  new_feature_count: newFeatures,
  spike_analysis: magnitude + "x spike on " + vars.spike_metric + ", " + newFeatures + " new features"
};`,
          outputVariables: [
            { name: "expansion_score", type: "number" },
            { name: "expansion_tier", type: "string" },
            { name: "potential_expansion_mrr", type: "number" },
            { name: "new_feature_count", type: "number" },
            { name: "spike_analysis", type: "string" },
          ],
        },
      }),

      // 4. Expansion context loader HubSpot
      hubspotGetNode(
        "4",
        "Load Expansion Context from HubSpot",
        760,
        "crm/v3/objects/contacts/{contact_email}?idProperty=email&properties=firstname,lastname,company,jobtitle,industry,lifecyclestage,hs_lead_status,num_associated_deals",
        [
          {
            name: "properties.firstname",
            customName: "first_name",
            type: "string",
          },
          {
            name: "properties.lastname",
            customName: "last_name",
            type: "string",
          },
          {
            name: "properties.jobtitle",
            customName: "job_title",
            type: "string",
          },
          {
            name: "properties.industry",
            customName: "industry",
            type: "string",
          },
          {
            name: "properties.num_associated_deals",
            customName: "existing_deals",
            type: "string",
          },
        ],
      ),

      // 5. AI opportunity assessor
      aiNode(
        "5",
        "AI Assess Expansion Opportunity",
        1000,
        "You are a customer expansion strategist. Given the usage spike data, expansion score, and customer context, craft an outreach strategy that acknowledges the growth, highlights the value they are getting, and naturally introduces expansion options. Be celebratory and helpful, not salesy. Respond JSON: {outreach_subject: string, outreach_body_html: string, expansion_pitch: string, recommended_upsell: string, conversation_starter: string, ae_briefing: string, expansion_urgency: string}",
        "Customer: {first_name} {last_name}, Title: {job_title}, Company: {company_name} ({industry}). Plan: {plan_type}, MRR: ${current_mrr}. Spike: {spike_magnitude}x on {spike_metric} ({baseline_usage} -> {current_usage}). New features: {new_features_used} ({new_feature_count} total). Expansion score: {expansion_score} ({expansion_tier}). Potential expansion: ${potential_expansion_mrr}/mo. Craft the outreach.",
        [
          { field: "outreach_subject", as: "outreach_subject", type: "string" },
          { field: "outreach_body_html", as: "outreach_body", type: "string" },
          { field: "expansion_pitch", as: "expansion_pitch", type: "string" },
          {
            field: "recommended_upsell",
            as: "recommended_upsell",
            type: "string",
          },
          {
            field: "conversation_starter",
            as: "conversation_starter",
            type: "string",
          },
          { field: "ae_briefing", as: "ae_briefing", type: "string" },
          {
            field: "expansion_urgency",
            as: "expansion_urgency",
            type: "string",
          },
        ],
      ),

      // 6. Value email
      emailNode(
        "6",
        "Send Usage Growth Value Email",
        1240,
        "{contact_email}",
        "{outreach_subject}",
        "{outreach_body}",
      ),

      // 7. AE alert Slack
      slackNode(
        "7",
        "Alert AE on Slack",
        1480,
        "Expansion Opportunity [{expansion_tier}]\\n*{company_name}* ({contact_name} — {job_title})\\nPlan: {plan_type} | MRR: ${current_mrr}\\nSpike: {spike_magnitude}x on {spike_metric}\\nNew Features: {new_features_used}\\nExpansion Score: {expansion_score}/100\\nPotential: +${potential_expansion_mrr}/mo\\nRecommended Upsell: {recommended_upsell}\\nConversation Starter: {conversation_starter}\\nBriefing: {ae_briefing}",
      ),

      // 8. CRM expansion opportunity HubSpot
      hubspotPostNode(
        "8",
        "Create Expansion Opportunity in HubSpot",
        1720,
        "crm/v3/objects/deals",
        [
          {
            key: "properties",
            value:
              '{"dealname":"Expansion: {company_name} — {recommended_upsell}","amount":"{potential_expansion_mrr}","dealstage":"appointmentscheduled","pipeline":"default","deal_source":"PRODUCT_USAGE_SPIKE","description":"Usage spike: {spike_magnitude}x on {spike_metric}. Current plan: {plan_type}, MRR: ${current_mrr}. Expansion score: {expansion_score} ({expansion_tier}). New features: {new_features_used}. Recommended: {recommended_upsell}. Urgency: {expansion_urgency}"}',
          },
        ],
        [
          { name: "id", customName: "expansion_deal_id", type: "string" },
          {
            name: "properties.dealname",
            customName: "expansion_deal_name",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1587. PROPOSAL FOLLOW UP SEQUENCE (8 nodes)
  // ===================================================================

  "Proposal Follow Up Sequence": {
    summary:
      "Fires when a proposal is sent via webhook, extracts proposal data with AI, tracks document behavior via API, uses AI to generate section-aware follow-up, sends a day 2 follow-up email, waits 3 days, routes via view pattern condition, and sends an escalation alert on Slack.",
    nodes: [
      trigger("Webhook: Proposal sent to prospect", "webhook_app", {
        source: "proposal_tool",
        event: "proposal.sent",
        howItStarts:
          "Fires when a sales proposal is sent to a prospect — including the proposal link, recipient details, deal context, and document tracking metadata from tools like PandaDoc, Proposify, or DocuSign",
        setup:
          "Configure proposal tool webhook for document sent events. Set OpenAI API key for AI extraction and follow-up generation. Provide document tracking API credentials. Configure email for follow-up sequences. Set Slack webhook for escalation alerts.",
        inputFields: [],
      }),

      // 2. AI extract proposal data
      aiNode(
        "2",
        "AI Extract Proposal Data",
        280,
        "You are a sales proposal parser. Extract the proposal title, recipient name and email, deal name, deal amount, proposal URL, sections included, expiry date, deal owner name and email, company name, and any custom terms from the raw webhook payload. Respond JSON: {proposal_title: string, recipient_name: string, recipient_email: string, deal_name: string, deal_amount: string, proposal_url: string, sections_included: string, expiry_date: string, deal_owner_name: string, deal_owner_email: string, company_name: string, custom_terms: string, proposal_id: string}",
        "Raw webhook payload: {webhook_payload}. Extract all proposal sending data regardless of field naming conventions.",
        [
          { field: "proposal_title", as: "proposal_title", type: "string" },
          { field: "recipient_name", as: "recipient_name", type: "string" },
          { field: "recipient_email", as: "recipient_email", type: "string" },
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "string" },
          { field: "proposal_url", as: "proposal_url", type: "string" },
          {
            field: "sections_included",
            as: "sections_included",
            type: "string",
          },
          { field: "expiry_date", as: "expiry_date", type: "string" },
          { field: "deal_owner_name", as: "deal_owner_name", type: "string" },
          { field: "deal_owner_email", as: "deal_owner_email", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "custom_terms", as: "custom_terms", type: "string" },
          { field: "proposal_id", as: "proposal_id", type: "string" },
        ],
      ),

      // 3. Document behavior tracker API
      n("3", "hitApi", "Track Document Viewing Behavior", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.pandadoc.com/public/v1/documents/{proposal_id}/analytics",
          headers: [
            { key: "Authorization", value: "Bearer {pandadoc_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "total_views", customName: "total_views", type: "number" },
            {
              name: "total_time_spent_seconds",
              customName: "time_spent_seconds",
              type: "number",
            },
            {
              name: "last_viewed_at",
              customName: "last_viewed_at",
              type: "string",
            },
            {
              name: "most_viewed_section",
              customName: "most_viewed_section",
              type: "string",
            },
            {
              name: "completion_pct",
              customName: "completion_pct",
              type: "string",
            },
            {
              name: "forwarded_to",
              customName: "forwarded_to",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI section-aware follow-up generator
      aiNode(
        "4",
        "AI Generate Section-Aware Follow-Up",
        760,
        "You are a sales proposal follow-up specialist. Given the proposal sections, document viewing behavior (which sections were viewed most, time spent, completion), and deal context, craft a follow-up email that specifically addresses the sections the prospect spent the most time on or skipped. If they focused on pricing, address value. If they focused on terms, address flexibility. Respond JSON: {follow_up_subject: string, follow_up_body_html: string, section_insights: string, objection_anticipation: string, next_step_suggestion: string, view_pattern_assessment: string, engagement_level: string}",
        "Proposal: {proposal_title} for {company_name} (${deal_amount}). Sections: {sections_included}. Views: {total_views}, Time: {time_spent_seconds}s, Completion: {completion_pct}%. Most viewed section: {most_viewed_section}. Forwarded to: {forwarded_to}. Expiry: {expiry_date}. Custom terms: {custom_terms}. Generate a section-aware follow-up.",
        [
          {
            field: "follow_up_subject",
            as: "follow_up_subject",
            type: "string",
          },
          {
            field: "follow_up_body_html",
            as: "follow_up_body",
            type: "string",
          },
          { field: "section_insights", as: "section_insights", type: "string" },
          {
            field: "objection_anticipation",
            as: "objection_anticipation",
            type: "string",
          },
          {
            field: "next_step_suggestion",
            as: "next_step_suggestion",
            type: "string",
          },
          {
            field: "view_pattern_assessment",
            as: "view_pattern_assessment",
            type: "string",
          },
          { field: "engagement_level", as: "engagement_level", type: "string" },
        ],
      ),

      // 5. Day 2 follow-up email
      emailNode(
        "5",
        "Send Day 2 Follow-Up Email",
        1000,
        "{recipient_email}",
        "{follow_up_subject}",
        "{follow_up_body}",
      ),

      // 6. Delay 3 days
      n("6", "delayAction", "Wait 3 Days for Engagement", 1240, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 3,
          delayUnit: "days",
          description:
            "Wait 3 days after the follow-up email to assess viewing patterns before determining escalation",
        },
      }),

      // 7. View pattern branch condition
      n("7", "condition", "Has Prospect Engaged with Proposal?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "total_views", operator: "gte", value: "2" },
          ],
        },
      }),

      // 8. Escalation alert Slack
      slackNode(
        "8",
        "Escalation Alert on Slack",
        1720,
        "Proposal Follow-Up Alert\\n*{proposal_title}* — {company_name} (${deal_amount})\\nRecipient: {recipient_name} ({recipient_email})\\nViews: {total_views} | Time: {time_spent_seconds}s | Completion: {completion_pct}%\\nMost Viewed: {most_viewed_section}\\nForwarded To: {forwarded_to}\\nEngagement: {engagement_level}\\nPattern: {view_pattern_assessment}\\nAnticipated Objection: {objection_anticipation}\\nExpiry: {expiry_date}\\nDeal Owner: {deal_owner_name} — {next_step_suggestion}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1588. PROPOSAL GENERATOR (9 nodes)
  // ===================================================================

  "Proposal Generator": {
    summary:
      "Fires when a deal enters proposal stage via webhook, extracts deal data with AI, pulls configuration pricing from HubSpot, fetches customer industry context from Clearbit API, drafts the proposal with AI, formats it with runCode, routes through internal review approval gate, sends the proposal delivery email, and logs to HubSpot CRM.",
    nodes: [
      trigger("Webhook: Deal enters proposal stage", "webhook_app", {
        source: "hubspot",
        event: "deal.stage_change_to_proposal",
        howItStarts:
          "Fires when a deal moves into proposal or quote stage in the CRM — triggering automated proposal generation with deal-specific pricing, customer context, and industry data",
        setup:
          "Configure HubSpot webhook for deal stage changes to proposal stage. Set OpenAI API key for AI extraction and proposal drafting. Provide HubSpot token for pricing data. Set Clearbit API key for industry context. Configure approval workflow and email for proposal delivery.",
        inputFields: [],
      }),

      // 2. AI extract deal data
      aiNode(
        "2",
        "AI Extract Deal Data for Proposal",
        280,
        "You are a CRM deal parser for proposal generation. Extract the deal name, deal ID, amount, products/line items, contact name and email, company name, deal owner name and email, close date, discount if any, payment terms, custom requirements, and the number of seats or licenses from the raw webhook payload. Respond JSON: {deal_name: string, deal_id: string, deal_amount: string, products: string, contact_name: string, contact_email: string, company_name: string, deal_owner_name: string, deal_owner_email: string, close_date: string, discount_pct: string, payment_terms: string, custom_requirements: string, seats_count: string}",
        "Raw webhook payload: {webhook_payload}. Extract all deal data for proposal generation regardless of field naming conventions.",
        [
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "string" },
          { field: "products", as: "products", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "deal_owner_name", as: "deal_owner_name", type: "string" },
          { field: "deal_owner_email", as: "deal_owner_email", type: "string" },
          { field: "close_date", as: "close_date", type: "string" },
          { field: "discount_pct", as: "discount_pct", type: "string" },
          { field: "payment_terms", as: "payment_terms", type: "string" },
          {
            field: "custom_requirements",
            as: "custom_requirements",
            type: "string",
          },
          { field: "seats_count", as: "seats_count", type: "string" },
        ],
      ),

      // 3. Configuration pricing puller HubSpot
      hubspotGetNode(
        "3",
        "Pull Configuration Pricing from HubSpot",
        520,
        "crm/v3/objects/deals/{deal_id}/associations/line_items?limit=20",
        [
          { name: "results[0].id", customName: "line_item_id", type: "string" },
          { name: "results", customName: "line_items_raw", type: "string" },
        ],
      ),

      // 4. Customer industry context Clearbit API
      n("4", "hitApi", "Fetch Industry Context from Clearbit", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={company_name}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name",
              customName: "clearbit_company_name",
              type: "string",
            },
            {
              name: "category.industry",
              customName: "company_industry",
              type: "string",
            },
            {
              name: "category.subIndustry",
              customName: "company_sub_industry",
              type: "string",
            },
            {
              name: "metrics.employees",
              customName: "company_employees",
              type: "string",
            },
            {
              name: "metrics.annualRevenue",
              customName: "company_revenue",
              type: "string",
            },
            {
              name: "description",
              customName: "company_description",
              type: "string",
            },
          ],
        },
      }),

      // 5. AI proposal drafter
      aiNode(
        "5",
        "AI Draft Proposal",
        1000,
        "You are a professional proposal writer. Given the deal details, product configuration, customer industry context, and custom requirements, draft a complete business proposal. Include an executive summary, problem statement tailored to the industry, proposed solution, pricing table, implementation timeline, ROI projections, terms, and a compelling call to action. Use professional tone. Respond JSON: {executive_summary: string, problem_statement: string, proposed_solution: string, pricing_section: string, implementation_timeline: string, roi_projection: string, terms_section: string, call_to_action: string, proposal_html: string, estimated_roi_pct: string}",
        "Company: {clearbit_company_name} ({company_industry} / {company_sub_industry}). Employees: {company_employees}. Revenue: {company_revenue}. Description: {company_description}. Contact: {contact_name}. Deal: {deal_name}, Amount: ${deal_amount}. Products: {products}. Seats: {seats_count}. Discount: {discount_pct}%. Payment terms: {payment_terms}. Custom requirements: {custom_requirements}. Close date: {close_date}. Draft a compelling proposal.",
        [
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          {
            field: "problem_statement",
            as: "problem_statement",
            type: "string",
          },
          {
            field: "proposed_solution",
            as: "proposed_solution",
            type: "string",
          },
          { field: "pricing_section", as: "pricing_section", type: "string" },
          {
            field: "implementation_timeline",
            as: "implementation_timeline",
            type: "string",
          },
          { field: "roi_projection", as: "roi_projection", type: "string" },
          { field: "terms_section", as: "terms_section", type: "string" },
          { field: "call_to_action", as: "call_to_action", type: "string" },
          { field: "proposal_html", as: "proposal_html", type: "string" },
          {
            field: "estimated_roi_pct",
            as: "estimated_roi_pct",
            type: "string",
          },
        ],
      ),

      // 6. Proposal formatter runCode
      n("6", "runCode", "Format Final Proposal Document", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Assemble the final proposal document from AI-generated sections
const sections = [
  { title: "Executive Summary", content: vars.executive_summary },
  { title: "The Challenge", content: vars.problem_statement },
  { title: "Proposed Solution", content: vars.proposed_solution },
  { title: "Pricing", content: vars.pricing_section },
  { title: "Implementation Timeline", content: vars.implementation_timeline },
  { title: "ROI Projection", content: vars.roi_projection },
  { title: "Terms & Conditions", content: vars.terms_section },
];
const sectionCount = sections.filter(s => s.content && s.content.length > 0).length;
const wordCount = sections.reduce((sum, s) => sum + (s.content || "").split(" ").length, 0);
const proposalTitle = "Proposal: " + vars.deal_name + " — " + vars.clearbit_company_name;
const validUntil = vars.close_date || new Date(Date.now() + 30*86400000).toISOString().split("T")[0];
return {
  proposal_title: proposalTitle,
  section_count: sectionCount,
  word_count: wordCount,
  valid_until: validUntil,
  format_status: "ready_for_review"
};`,
          outputVariables: [
            { name: "proposal_title", type: "string" },
            { name: "section_count", type: "number" },
            { name: "word_count", type: "number" },
            { name: "valid_until", type: "string" },
            { name: "format_status", type: "string" },
          ],
        },
      }),

      // 7. Internal review approval gate
      n("7", "approvalGate", "Internal Review Before Sending", 1480, {
        approvalGateData: {
          message:
            "Proposal Review Required\\n\\nTitle: {proposal_title}\\nCompany: {clearbit_company_name} ({company_industry})\\nDeal: {deal_name} — ${deal_amount}\\nContact: {contact_name} ({contact_email})\\nSections: {section_count} | Words: {word_count}\\nEstimated ROI: {estimated_roi_pct}%\\nValid Until: {valid_until}\\nDiscount: {discount_pct}% | Terms: {payment_terms}\\n\\nReview the proposal content and approve for delivery.",
        },
      }),

      // 8. Proposal delivery email
      emailNode(
        "8",
        "Send Proposal Delivery Email",
        1720,
        "{contact_email}",
        "{contact_name}, your customized proposal for {company_name}",
        "<p>Hi {contact_name},</p><p>Thank you for the opportunity to put together a proposal for {clearbit_company_name}. We are excited about the potential of working together.</p><h3>{proposal_title}</h3><p><strong>Executive Summary:</strong> {executive_summary}</p><p>{call_to_action}</p><p><strong>Proposal Highlights:</strong></p><ul><li>Total Investment: ${deal_amount}</li><li>Estimated ROI: {estimated_roi_pct}%</li><li>Implementation: {implementation_timeline}</li><li>Products: {products} ({seats_count} seats)</li></ul><p>This proposal is valid until {valid_until}. We would love to walk through it together — <a href='https://calendly.com/your-team/proposal-review?email={contact_email}'>schedule a review call</a>.</p><p>Best regards,<br/>{deal_owner_name}</p>",
      ),

      // 9. CRM logger HubSpot
      hubspotPostNode(
        "9",
        "Log Proposal in HubSpot",
        1960,
        "crm/v3/objects/notes",
        [
          {
            key: "properties",
            value:
              '{"hs_note_body":"Proposal sent: {proposal_title}\\nAmount: ${deal_amount} | Discount: {discount_pct}%\\nProducts: {products} ({seats_count} seats)\\nROI: {estimated_roi_pct}%\\nValid until: {valid_until}\\nSections: {section_count} | Words: {word_count}\\nTerms: {payment_terms}\\nCustom requirements: {custom_requirements}","hs_timestamp":"' +
              new Date().toISOString() +
              '"}',
          },
          {
            key: "associations",
            value:
              '[{"to":{"id":"{deal_id}"},"types":[{"associationCategory":"HUBSPOT_DEFINED","associationTypeId":214}]}]',
          },
        ],
        [{ name: "id", customName: "proposal_note_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1589. PROPOSAL SENT FOLLOW-UP (7 nodes)
  // ===================================================================

  "Proposal Sent Follow-Up": {
    summary:
      "Fires when a proposal is delivered via webhook, extracts proposal data with AI, loads prospect deal context from HubSpot, sends an immediate confirmation email, waits 3 days, checks engagement via condition, and sends an escalating reminder email.",
    nodes: [
      trigger("Webhook: Proposal delivered to prospect", "webhook_app", {
        source: "proposal_tool",
        event: "proposal.delivered",
        howItStarts:
          "Fires when a sales proposal is confirmed delivered to the prospect — including read receipts, document view confirmations, or email delivery confirmations from tools like PandaDoc, Proposify, or DocuSign",
        setup:
          "Configure proposal tool webhook for document delivery events. Set OpenAI API key for AI extraction. Provide HubSpot token for deal context. Configure email for confirmation and follow-up sequences.",
        inputFields: [],
      }),

      // 2. AI extract proposal data
      aiNode(
        "2",
        "AI Extract Delivered Proposal Data",
        280,
        "You are a sales proposal delivery parser. Extract the proposal title, recipient name and email, deal name, deal amount, delivery timestamp, proposal URL, expiry date, deal owner name and email, company name, and whether the proposal has been opened from the raw webhook payload. Respond JSON: {proposal_title: string, recipient_name: string, recipient_email: string, deal_name: string, deal_amount: string, delivery_time: string, proposal_url: string, expiry_date: string, deal_owner_name: string, deal_owner_email: string, company_name: string, has_been_opened: string, proposal_id: string}",
        "Raw webhook payload: {webhook_payload}. Extract all proposal delivery data regardless of field naming conventions.",
        [
          { field: "proposal_title", as: "proposal_title", type: "string" },
          { field: "recipient_name", as: "recipient_name", type: "string" },
          { field: "recipient_email", as: "recipient_email", type: "string" },
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "string" },
          { field: "delivery_time", as: "delivery_time", type: "string" },
          { field: "proposal_url", as: "proposal_url", type: "string" },
          { field: "expiry_date", as: "expiry_date", type: "string" },
          { field: "deal_owner_name", as: "deal_owner_name", type: "string" },
          { field: "deal_owner_email", as: "deal_owner_email", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "has_been_opened", as: "has_been_opened", type: "string" },
          { field: "proposal_id", as: "proposal_id", type: "string" },
        ],
      ),

      // 3. Prospect deal context HubSpot
      hubspotGetNode(
        "3",
        "Load Prospect Deal Context from HubSpot",
        520,
        "crm/v3/objects/contacts/{recipient_email}?idProperty=email&properties=firstname,lastname,company,jobtitle,industry,lifecyclestage,num_associated_deals",
        [
          {
            name: "properties.firstname",
            customName: "first_name",
            type: "string",
          },
          {
            name: "properties.lastname",
            customName: "last_name",
            type: "string",
          },
          {
            name: "properties.jobtitle",
            customName: "job_title",
            type: "string",
          },
          {
            name: "properties.industry",
            customName: "industry",
            type: "string",
          },
          {
            name: "properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
        ],
      ),

      // 4. Immediate confirmation email
      emailNode(
        "4",
        "Send Immediate Confirmation Email",
        760,
        "{recipient_email}",
        "{first_name}, your proposal for {company_name} has been delivered",
        "<p>Hi {first_name},</p><p>Just a quick note to confirm that your proposal has been delivered successfully:</p><p><strong>{proposal_title}</strong></p><ul><li><strong>Investment:</strong> ${deal_amount}</li><li><strong>Valid Until:</strong> {expiry_date}</li><li><strong>Proposal Link:</strong> <a href='{proposal_url}'>View your proposal</a></li></ul><p>Take your time reviewing it. If you have any questions or would like to discuss any of the details, {deal_owner_name} is available to walk through it with you.</p><p><a href='https://calendly.com/your-team/proposal-review?email={recipient_email}'>Schedule a review call</a></p><p>We are excited about the opportunity to work with {company_name}!</p><p>Best regards,<br/>{deal_owner_name}</p>",
      ),

      // 5. Delay 3 days
      n("5", "delayAction", "Wait 3 Days for Engagement", 1000, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 3,
          delayUnit: "days",
          description:
            "Wait 3 days after proposal delivery to check engagement before sending a follow-up",
        },
      }),

      // 6. Engagement monitor condition
      n("6", "condition", "Has Proposal Been Opened?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "has_been_opened", operator: "eq", value: "true" },
          ],
        },
      }),

      // 7. Escalating reminder email
      emailNode(
        "7",
        "Send Escalating Reminder Email",
        1480,
        "{recipient_email}",
        "{first_name}, checking in on the {proposal_title} proposal",
        "<p>Hi {first_name},</p><p>Just following up on the proposal we sent over for {company_name}. We want to make sure you have everything you need to make a decision.</p><p>Quick recap:</p><ul><li><strong>Proposal:</strong> {proposal_title}</li><li><strong>Investment:</strong> ${deal_amount}</li><li><strong>Expiry:</strong> {expiry_date}</li></ul><p>If you have any concerns about pricing, scope, or timeline, we are happy to discuss adjustments. Many teams at this stage find it helpful to schedule a 15-minute call to go through the details together.</p><p><a href='{proposal_url}'>Review the proposal</a> | <a href='https://calendly.com/your-team/proposal-review?email={recipient_email}'>Schedule a review call</a></p><p>Looking forward to hearing from you!</p><p>Best regards,<br/>{deal_owner_name}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_2"),
    ],
  },
};
